import { CommonModule } from "@angular/common";
import { BreakpointObserver } from "@angular/cdk/layout";
import { Component, computed, DestroyRef, effect, ElementRef, HostListener, inject, input, OnInit, signal, untracked, ViewChild } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { map } from "rxjs/operators";
import { FormsModule } from "@angular/forms";
import { Subscription } from "rxjs";
import { AgentProfile } from "@models/agent";
import { Widget, WidgetMetaData, WidgetPosition } from "@models/widget";
import { ButtonModule } from 'primeng/button';

import { ChatMessageKind, IframeEvent } from "../angular-contracts";
import { Message } from "../models/chat";
import {
    ConversationTranscriptRole,
    formatTranscriptDateDividerLabel,
    isBrowserPreviewableAudioAttachment,
    isBrowserPreviewableVideoAttachment,
    transcriptLocalDayKey,
} from "../common/conversation-transcript-thread/conversation-transcript.util";
import { ParseMessageHtmlPipe } from "../pipes/parse-message-html.pipe";
import {
    CallService,
    isImageContentTranscriptionEcho,
    type HumanAgentPresentEvent,
    type RemoteTypingParticipant,
} from "../services/calls.service";
import { WidgetService } from "../services/widget.service";
import { chatWidgetType } from './../models/chat';
import { environment } from "@environments/environment";
import {
    AsYouType,
    getCountryCallingCode,
    isSupportedCountry,
    Metadata,
    parsePhoneNumberFromString,
    type CountryCode,
} from 'libphonenumber-js';
import { getPhoneCountryOptions, type PhoneCountryOption } from './widget-phone-countries';

@Component({
    selector: 'app-widget-host',
    templateUrl: './widget-host.component.html',
    imports:[FormsModule, CommonModule, ButtonModule, ParseMessageHtmlPipe],
    styles: `
    :host {
      display: block;
      background: transparent !important;
    }
    .tg-widget-host {
      --tg-accent: #6366f1;
      --tg-widget-max-panel-height: min(
        680px,
        calc(
          100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 2.5rem
        )
      );
      background: transparent !important;
    }
    .tg-widget-launcher-btn {
      -webkit-appearance: none;
      appearance: none;
      padding: 0;
      border: none;
      background-color: transparent;
      border-radius: 9999px;
      overflow: hidden;
    }
    .tg-widget-launcher-btn:focus-visible {
      outline: none;
    }
    .tg-widget-launcher-img {
      object-fit: cover;
      object-position: center;
      border-radius: 9999px;
    }
    .tg-icon-btn {
      width: 2.25rem; height: 2.25rem; display: flex; align-items: center; justify-content: center;
      border-radius: 9999px; color: #a3a3a3; flex-shrink: 0; border: none; background: transparent;
      cursor: pointer; transition: color 0.15s, background 0.15s;
    }
    .tg-icon-btn:hover { color: #e5e5e5; background: rgba(255,255,255,0.06); }
    .tg-scroll-dark { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.12) transparent; }
    .tg-scroll-dark::-webkit-scrollbar { width: 6px; }
    .tg-scroll-dark::-webkit-scrollbar-thumb {
      background: rgba(255,255,255,0.12); border-radius: 6px;
    }
    .tg-scroll-light { scrollbar-width: thin; scrollbar-color: rgba(0,0,0,0.12) transparent; }
    .tg-scroll-light::-webkit-scrollbar { width: 6px; }
    .tg-scroll-light::-webkit-scrollbar-thumb {
      background: rgba(0,0,0,0.12); border-radius: 6px;
    }
    [data-widget-theme="light"] .tg-icon-btn { color: #52525b; }
    [data-widget-theme="light"] .tg-icon-btn:hover { color: #18181b; background: rgba(0,0,0,0.06); }
    .tg-prechat-card {
      box-shadow: 0 4px 24px -6px rgba(15, 23, 42, 0.08), 0 0 0 1px rgba(15, 23, 42, 0.04);
    }
    [data-widget-theme="dark"] .tg-prechat-card {
      box-shadow: 0 8px 32px -8px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255,255,255,0.04);
    }
    .tg-prechat-input:focus-visible {
      outline: none;
    }
    @keyframes ping {
                75%, 100% {
                  transform: scale(2);
                  opacity: 0;
                }
              }
              
              .animate-ping {
                animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
              }
              
              .animation-delay-0 { animation-delay: 0ms; }
              .animation-delay-300 { animation-delay: 300ms; }
              .animation-delay-600 { animation-delay: 600ms; }
              
              @keyframes bounce {
                0%, 100% {
                  transform: translateY(0);
                  animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
                }
                50% {
                  transform: translateY(-25%);
                  animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
                }
              }
              
              .animate-bounce {
                animation: bounce 1s infinite;
              }

              .tg-wa-thinking-bubble {
                animation: tg-wa-thinking-glow 2.4s ease-in-out infinite;
              }
              @keyframes tg-wa-thinking-glow {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.92; }
              }
              @keyframes tg-dot-wave-deep {
                0%, 60%, 100% { transform: translateY(0); opacity: 0.28; }
                30% { transform: translateY(-4px); opacity: 1; }
              }
              .tg-wa-deep-dots .tg-typing-dot {
                animation: tg-dot-wave-deep 1.4s ease-in-out infinite;
              }
              .tg-wa-deep-dots .tg-typing-dot:nth-child(2) { animation-delay: 0.22s; }
              .tg-wa-deep-dots .tg-typing-dot:nth-child(3) { animation-delay: 0.44s; }
              @keyframes tg-dot-wave {
                0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
                30% { transform: translateY(-3px); opacity: 1; }
              }
              .tg-typing-dot {
                width: 5px;
                height: 5px;
                border-radius: 9999px;
                background: currentColor;
                animation: tg-dot-wave 1.05s ease-in-out infinite;
              }
              .tg-typing-dot:nth-child(2) { animation-delay: 0.15s; }
              .tg-typing-dot:nth-child(3) { animation-delay: 0.3s; }
              /* Voice recording waveform bars */
              @keyframes tg-voice-bar {
                0%, 100% { transform: scaleY(0.35); opacity: 0.5; }
                50% { transform: scaleY(1); opacity: 1; }
              }
              .tg-voice-bar {
                transform-origin: bottom center;
                animation: tg-voice-bar 0.8s ease-in-out infinite;
              }
              /* Chat bubble HTML (innerHTML): lists + paragraphs */
              :host ::ng-deep .tg-chat-msg { word-break: break-word; }
              :host ::ng-deep .tg-chat-msg > p.tg-chat-p {
                margin: 0 0 0.55rem 0;
                line-height: 1.55;
              }
              :host ::ng-deep .tg-chat-msg > p.tg-chat-p:last-child { margin-bottom: 0; }
              :host ::ng-deep .tg-chat-msg > ul.tg-chat-ul,
              :host ::ng-deep .tg-chat-msg > ol.tg-chat-ol {
                margin: 0.25rem 0 0.6rem 0;
                padding-left: 1.2rem;
                list-style-position: outside;
              }
              :host ::ng-deep .tg-chat-msg > ul.tg-chat-ul { list-style-type: disc; }
              :host ::ng-deep .tg-chat-msg > ol.tg-chat-ol { list-style-type: decimal; }
              :host ::ng-deep .tg-chat-msg li.tg-chat-li {
                margin: 0.28rem 0;
                padding-left: 0.2rem;
                line-height: 1.5;
              }
              :host ::ng-deep .tg-chat-msg > ul.tg-chat-ul:last-child,
              :host ::ng-deep .tg-chat-msg > ol.tg-chat-ol:last-child { margin-bottom: 0; }
              :host ::ng-deep .tg-chat-msg strong { font-weight: 600; }
              @keyframes tg-launcher-badge-pop {
                from { transform: scale(0.65); opacity: 0; }
                to { transform: scale(1); opacity: 1; }
              }
              .tg-launcher-unread-badge {
                pointer-events: none;
                position: absolute;
                top: 4px;
                right: 4px;
                z-index: 3;
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 1.375rem;
                height: 1.375rem;
                padding: 0 6px;
                border-radius: 9999px;
                font-size: 0.6875rem;
                font-weight: 700;
                letter-spacing: -0.02em;
                line-height: 1;
                color: #fff;
                font-variant-numeric: tabular-nums;
                background: linear-gradient(145deg, #fb7185 0%, #e11d48 55%, #be123c 100%);
                box-shadow:
                  0 2px 6px rgba(190, 18, 60, 0.45),
                  0 0 0 2px rgba(15, 23, 42, 0.92),
                  0 1px 2px rgba(15, 23, 42, 0.12);
                 animation: tg-launcher-badge-pop 0.28s cubic-bezier(0.34, 1.45, 0.64, 1) both;
               }
               @keyframes tg-popup-fade-in-up {
                 0% { opacity: 0; transform: translateY(8px) scale(0.95); }
                 100% { opacity: 1; transform: translateY(0) scale(1); }
               }
               .animate-popup-in {
                 animation: tg-popup-fade-in-up 0.35s cubic-bezier(0.21, 1.02, 0.73, 1) both;
               }
     `,
    standalone: true,
})
export class WidgetComponent implements OnInit {
    readonly chatMessageKind = ChatMessageKind;

    private static readonly MESSAGE_SOUND_STORAGE_KEY = 'tg_widget_message_sound';
    /** Public notification clip (GCS); avoids bundling mp3 in the widget. */
    private static readonly MESSAGE_SOUND_URL =
        'https://storage.googleapis.com/talkgenie-public/files/universfield-new-notification-09-352705.mp3';
    /** 1×1 transparent GIF: img src until embed config arrives so default icon does not flash before `imageUrl`. */
    private static readonly WIDGET_AVATAR_PENDING_SRC =
        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

    private readonly callSvc = inject(CallService);
    private readonly widgetSvc = inject(WidgetService);
    private readonly destroyRef = inject(DestroyRef);


    // Input signal
    position = input<'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'>('bottom-right');

    /** Embedded in the agent widget editor: live config from the form, no iframe parent. */
    dashboardPreviewMode = input(false);
    dashboardPreviewConfig = input<Record<string, unknown> | null>(null);
    /** Vertical alignment of the preview widget inside dashboard preview container. */
    dashboardPreviewVerticalAlign = input<'end' | 'center'>('end');
    /** `style-only`: no network/session side-effects, render local mock conversation shell. */
    dashboardPreviewBehavior = input<'style-only' | 'interactive-live'>('interactive-live');
    /** Style-only preview screen selection from builder UI. */
    dashboardPreviewScreen = input<'welcome' | 'conversation' | 'bubble'>('conversation');
    /** When preview mode, open the panel once so branding is visible without an extra click. */
    dashboardPreviewAutoOpen = input(true);

    private dashboardPreviewAutoOpened = false;
    /** Match embed: first-time visitors see pre-chat before Start buttons. */
    private dashboardPreviewPreChatGateDone = false;
    private dashboardStylePreviewInitialized = false;
    private lastPreviewBehavior: 'style-only' | 'interactive-live' | null = null;
  
    // State signals
    isOpen = signal(typeof window !== 'undefined' && window.parent !== window);
    /** Popup message visible (after delay when collapsed). */
    popupVisible = signal(false);

    /** Below app `sm` breakpoint (767px); matches talkgeniejs iframe fullscreen threshold. */
    private readonly breakpointObserver = inject(BreakpointObserver);
    readonly isMobileWidgetViewport = toSignal(
        this.breakpointObserver.observe("(max-width: 766px)").pipe(map((r) => r.matches)),
        { initialValue: false },
    );
    readonly widgetMobileFullscreenOpen = computed(() => this.isMobileWidgetViewport() && this.isOpen());

    /** Standalone widget page (not inside talkgeniejs embed host). */
    readonly isEmbeddedWidget = typeof window !== 'undefined' && window.parent !== window;
    readonly isStyleOnlyDashboardPreview = computed(
        () => this.dashboardPreviewMode() && this.dashboardPreviewBehavior() === 'style-only',
    );
    /** New assistant/human chat lines received while launcher is minimized (text session). */
    unreadWhileMinimized = signal(0);
    sessionStarted = signal(false);
    endCall = signal(true);
    responses = signal<Message[]>([]);
    /**
     * Visitor email for the chat session being started — used with widget session JWT to load recent history after {@link onSessionStarted}.
     */
    private sessionVisitorEmailForHistory: string | null = null;
    selectedProfile = signal<AgentProfile | null>(null);
    userTextMessage = signal('');
    isUserSpeaking = signal(true);
    chatMode = signal<string>('');
    isMuted = signal(false);
    isSocketRequestInProcess = signal(false);
    isHttpRequestInProcess = signal(true);
    chatWidgetType = chatWidgetType;
    widgetConfig = signal<Widget | null>(null);
    isHumanAgentPresent = signal(false);
    /** LiveKit identity → display name for everyone currently in the chat (header lists all). */
    private activeStaffByIdentity = signal<Record<string, string>>({});
    private nextAnonStaffKey = 0;
    private visitorHumanActive = false;
    /** LiveKit identity → display label for remote staff currently typing (visitor widget). */
    private humanAgentsTypingByIdentity = signal<Record<string, string>>({});
    /** True when at least one human agent is typing. */
    humanAgentTyping = computed(() => Object.keys(this.humanAgentsTypingByIdentity()).length > 0);
    /** AI agent busy (worker {@link TYPING_DATA_TOPIC} from agent participant). */
    aiAssistantTyping = signal(false);
    /** Worker phase: tools = KB/calendar/etc.; generating = LLM only. */
    agentTypingPhase = signal<'generating' | 'tools' | null>(null);
    /** Optional status line while tools run (e.g. knowledge base). */
    agentTypingHint = signal<string | null>(null);
    /** Incoming message sound (AI or human agent); persisted in localStorage. */
    soundNotificationsEnabled = signal(WidgetComponent.readWidgetSoundPreferenceFromStorage());
    private messageSoundEl: HTMLAudioElement | null = null;
    private messageSoundPreviewTimer: number | null = null;
    /** One muted play/pause per session so unmuted incoming sounds are allowed (autoplay policy). */
    private messageSoundGesturePrimed = false;
    private visitorTypingIdleTimer: ReturnType<typeof setTimeout> | null = null;
    private visitorTypingOutbound = false;
    
    @ViewChild('chatScrollContainer') chatScrollContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('countryPickerRoot', { read: ElementRef }) countryPickerRoot?: ElementRef<HTMLElement>;
    private shouldScroll = signal(false);
    
    /** Pre-chat: full name, email, country + local phone (Chatwoot-style) */
    preChatFullName = signal('');
    preChatEmail = signal('');
    /** ISO 3166-1 alpha-2 — dial code from {@link getCountryCallingCode} */
    preChatCountryIso = signal<CountryCode>('US');
    preChatPhoneLocal = signal('');
    /**
     * Once the country has been resolved by the user (manual pick, typing, or a
     * stored number), the async geo/IP auto-detection must not override it.
     */
    private countryAutoDetectLocked = false;
    /**
     * libphonenumber's own metadata, used to resolve the "main" country for a
     * calling code shared by several regions (e.g. +1 → US, +44 → GB, +7 → RU).
     * `getCountryCodeForCallingCode` isn't in the public typings, so we narrow
     * to just the method we need instead of hardcoding a country list ourselves.
     */
    private readonly phoneMetadata = new Metadata() as unknown as {
        getCountryCodeForCallingCode(callingCode: string): CountryCode | undefined;
    };
    showPreChatForm = signal(false);
    /** Searchable country dropdown (all libphonenumber regions) */
    readonly phoneCountries = getPhoneCountryOptions();
    countrySearchQuery = signal('');
    countryDropdownOpen = signal(false);
    preChatError = signal('');
    isPreChatValid = signal(false);

    /** Human takeover: visitor uploading a file to GCS via public signed URL */
    attachmentUploading = signal(false);
    attachmentError = signal('');
    /** File picked for attach; upload/send only after visitor confirms (preview step). */
    visitorAttachmentPreviewFile = signal<File | null>(null);
    visitorAttachmentPreviewUrl = signal<string | null>(null);
    visitorAttachmentPreviewIsImage = signal(false);
    visitorAttachmentPreviewIsVideo = signal(false);
    lightboxImageUrl = signal<string | null>(null);
    /** Widget session JWT from {@code initiate-chatwidget-connection} (attachments + recent history; binds email server-side). */
    private widgetAttachmentUploadToken: string | null = null;
    /** Pending parent-proxy requests for signed attachment upload URLs (embed iframe → talkgeniejs). */
    private readonly pendingAttachmentUploadRequests = new Map<
        string,
        {
            resolve: (value: { uploadUrl: string; publicUrl: string }) => void;
            reject: (reason: Error) => void;
            timeoutId: ReturnType<typeof setTimeout>;
        }
    >();
    /** Pending parent-proxy requests for recent conversation history (embed iframe → talkgeniejs). */
    private readonly pendingRecentConversationsRequests = new Map<
        string,
        {
            resolve: (value: {
                callSid: string;
                callType: string;
                startedAt: string | null;
                messages: { role: string; message: string; senderName?: string }[];
            }[]) => void;
            reject: (reason: Error) => void;
            timeoutId: ReturnType<typeof setTimeout>;
        }
    >();
    /** Pending parent-proxy feedback submissions (embed iframe → talkgeniejs). */
    private readonly pendingFeedbackRequests = new Map<
        string,
        {
            resolve: () => void;
            reject: (reason: Error) => void;
            timeoutId: ReturnType<typeof setTimeout>;
        }
    >();

    /** Voice recording state for AI chat */
    isVoiceRecording = signal(false);
    voiceRecordingSeconds = signal(0);
    voiceProcessing = signal(false);
    voiceError = signal('');
    /** Recorded blob waiting for user to send or discard (two-step flow). */
    voicePreviewBlob = signal<Blob | null>(null);
    voicePreviewBlobUrl = signal<string | null>(null);
    voicePreviewDurationSec = signal(0);
    private mediaRecorder: MediaRecorder | null = null;
    private audioChunks: BlobPart[] = [];
    private recordingTimerRef: ReturnType<typeof setInterval> | null = null;
    /** LiveKit room name returned at session start — doubles as callSid for feedback. */
    private sessionRoomName: string | null = null;

    // Header three-dots menu
    showHeaderMenu = signal(false);

    // Feedback form state
    showFeedbackForm = signal(false);
    feedbackRating = signal<number>(0);
    feedbackIsResolved = signal<boolean | null>(null);
    feedbackComment = signal('');
    feedbackSubmitting = signal(false);
    feedbackSubmitted = signal(false);

    @ViewChild('visitorAttachmentInput') visitorAttachmentInput?: ElementRef<HTMLInputElement>;

    /** Subtitle under brand (Chatwoot-style) */
    readonly preChatStatusLine = 'Typically replies in a few seconds';

    /** Bold line above the form */
    preChatLeadIn(): string {
        return 'Share your queries with us.';
    }


    /**
     * Used on the welcome screen so button click shows immediate feedback.
     * Cleared when `onSessionStarted()` fires.
     */
    startingChatMode = signal<string | null>(null);

    // Computed signals
    readonly hasResponses = computed(() => this.responses().length > 0);
    readonly canSendMessage = computed(
        () => this.userTextMessage().trim().length > 0 || !!this.visitorAttachmentPreviewFile(),
    );

    readonly composerPlaceholder = computed(() => {
        if (this.visitorAttachmentPreviewFile()) {
            return 'Add a caption (optional)';
        }
        return this.widgetConfig()?.metaData?.messagePlaceholder || 'Type a message';
    });
    readonly isChatMode = computed(() => this.chatMode() === chatWidgetType.ChatWidgetText);
    readonly isAudioMode = computed(() => this.chatMode() === chatWidgetType.ChatWidgetAudio);
    /** Comma-separated names for header, e.g. "Ali and Sara" or "Ali, Sara, and Fahad". */
    readonly botDisplayName = computed(
        () => this.widgetConfig()?.metaData?.chatbotDisplayName?.trim() || 'Assistant',
    );

    /**
     * Bot/launcher avatar: non-default only after {@link widgetConfig} is set; then custom `imageUrl` or bundled default.
     * While config is still null (iframe waiting for parent postMessage), uses a transparent pixel to avoid default-then-custom flicker.
     */
    readonly widgetBotAvatarSrc = computed(() => {
        const cfg = this.widgetConfig();
        if (cfg === null) {
            return WidgetComponent.WIDGET_AVATAR_PENDING_SRC;
        }
        const u = cfg.imageUrl?.trim();
        return u && u.length > 0 ? u : 'https://app.talkgenie.ai/assets/icon_white.png';
    });

    readonly connectedHumanAgentsSubtitle = computed(() => {
        const names = Object.values(this.activeStaffByIdentity()).filter((n) => n?.trim());
        if (names.length === 0) return null;
        if (names.length === 1) return names[0]!;
        if (names.length === 2) return `${names[0]} and ${names[1]}`;
        return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
    });

    /** Names of staff currently typing (from LiveKit typing packets), for the typing indicator line. */
    readonly humanTypingAgentName = computed(() => {
        const labels = Object.values(this.humanAgentsTypingByIdentity())
            .map((n) => n.trim())
            .filter(Boolean);
        return this.formatHumanTypingNames(labels);
    });

    readonly filteredPhoneCountries = computed(() => {
        const q = this.countrySearchQuery().trim().toLowerCase();
        if (!q) {
            return this.phoneCountries;
        }
        return this.phoneCountries.filter(
            (c) =>
                c.iso2.toLowerCase().includes(q) ||
                c.name.toLowerCase().includes(q) ||
                c.dial.includes(q),
        );
    });

    // Subscription for onSocketConnected - created per session, cleaned up in endSession
    private socketConnectedSub?: Subscription;

    // Convert observables to signals
    private readonly transcription$ = toSignal(
        this.callSvc.onTranscription.pipe(takeUntilDestroyed(this.destroyRef)),
        { initialValue: null }
    );

    private readonly agentAiTextStream$ = toSignal(
        this.callSvc.onAgentAiTextStream.pipe(takeUntilDestroyed(this.destroyRef)),
        { initialValue: null as { text: string; done: boolean } | null },
    );

    private readonly userSpeaking$ = toSignal(
        this.callSvc.onUserSpeaking.pipe(takeUntilDestroyed(this.destroyRef)),
        { initialValue: null }
    );

    private readonly humanAgentPresent$ = toSignal(
        this.callSvc.onHumanAgentPresent.pipe(takeUntilDestroyed(this.destroyRef)),
        { initialValue: undefined as HumanAgentPresentEvent | undefined },
    );

    private readonly humanAgentTextResponse$ = toSignal(
        this.callSvc.onHumanAgentTextResponse.pipe(takeUntilDestroyed(this.destroyRef)),
        { initialValue: null }
    );

    constructor() {
        // Effect to handle transcriptions (voice STT echo, etc.). Image turns use optimistic UI +
        // visitor_lk_chat; lk.transcription ImageContent repr is filtered in CallService.
        effect(() => {
            const transcription = this.transcription$();
            if (!transcription || isImageContentTranscriptionEcho(transcription)) {
                return;
            }
            const t = Date.now();
            this.responses.update((messages) => [
                ...messages,
                { text: transcription, fromAi: false, sentAt: t },
            ]);
            this.shouldScroll.set(true);
        });

        // AI text: incremental lk.transcription stream (cumulative chunks), then finalize
        effect(() => {
            const ev = this.agentAiTextStream$();
            if (!ev) return;
            const t = Date.now();
            if (!ev.done) {
                this.aiAssistantTyping.set(false);
                this.agentTypingPhase.set(null);
                this.agentTypingHint.set(null);
                this.responses.update((messages) => {
                    const last = messages[messages.length - 1];
                    if (
                        last?.fromAi &&
                        !last?.fromHuman &&
                        !last?.kind &&
                        last?.streaming
                    ) {
                        return [
                            ...messages.slice(0, -1),
                            { ...last, text: ev.text, streaming: true, sentAt: last.sentAt ?? t },
                        ];
                    }
                    return [
                        ...messages,
                        {
                            text: ev.text,
                            fromAi: true,
                            fromHuman: false,
                            streaming: true,
                            sentAt: t,
                        },
                    ];
                });
                this.shouldScroll.set(true);
                return;
            }
            this.responses.update((messages) => {
                const last = messages[messages.length - 1];
                if (last?.streaming && last.fromAi && !last.fromHuman) {
                    return [
                        ...messages.slice(0, -1),
                        {
                            ...last,
                            text: ev.text,
                            streaming: false,
                            sentAt: last.sentAt ?? t,
                        },
                    ];
                }
                if (!ev.text?.trim()) return messages;
                return [
                    ...messages,
                    { text: ev.text, fromAi: true, fromHuman: false, sentAt: t },
                ];
            });
            this.shouldScroll.set(true);
            if (ev.text?.trim()) {
                this.maybePlayIncomingMessageSound();
                this.noteIncomingAssistantMessageWhileMinimized();
            }
        });

        // Effect to handle human agent text responses (payload may include sender name)
        effect(() => {
            const payload = this.humanAgentTextResponse$();
            if (payload) {
                const text = typeof payload === 'string' ? payload : payload.message;
                const senderName = typeof payload === 'object' && payload.name ? payload.name : undefined;
                const senderImageUrl =
                    typeof payload === 'object' && payload.image && String(payload.image).trim()
                        ? String(payload.image).trim()
                        : undefined;
                const t = Date.now();
                this.responses.update((messages) => [
                    ...messages,
                    {
                        text,
                        fromAi: true,
                        fromHuman: true,
                        ...(senderName ? { senderName } : {}),
                        ...(senderImageUrl ? { senderImageUrl } : {}),
                        sentAt: t,
                    },
                ]);
                this.shouldScroll.set(true);
                if (text?.trim()) {
                    this.maybePlayIncomingMessageSound();
                    this.noteIncomingAssistantMessageWhileMinimized();
                }
            }
        });

        // Effect to handle user speaking state
        effect(() => {
            const isSpeaking = this.userSpeaking$();
            if (isSpeaking !== null) {
                this.isUserSpeaking.set(isSpeaking);
            }
        });

        // Human takeover + extra agents.
        // - human_arrived (RPC) → additionalJoin: always one join row (spectator or extra agent).
        // - takeover_start (RPC) → present: true: one join row per new LiveKit identity (AI paused); not name-based.
        effect(() => {
            const ev = this.humanAgentPresent$();
            if (ev === undefined) return;

            if ('humanLeft' in ev && ev.humanLeft) {
                const label = ev.agentName?.trim() || 'A team member';
                if (!ev.lastHuman) {
                    this.removeStaffFromActiveMap(ev.leftIdentity, ev.agentName);
                }
                if (ev.lastHuman) {
                    this.isHumanAgentPresent.set(false);
                    this.activeStaffByIdentity.set({});
                    this.nextAnonStaffKey = 0;
                    this.visitorHumanActive = false;
                    // this.responses.update((messages) => [
                    //     ...messages,
                    //     {
                    //         kind: ChatMessageKind.HumanLeft,
                    //         text: `${label} left — you're back with the AI assistant.`,
                    //         leftAgentName: label,
                    //         fromAi: false,
                    //         fromHuman: false,
                    //     },
                    // ]);
                } 
                this.shouldScroll.set(true);
                // this.maybePlayIncomingMessageSound();
                return;
            }

            if ('additionalJoin' in ev && ev.additionalJoin) {
                this.addStaffToActiveMap(ev.agentIdentity, ev.agentName);
                const name = ev.agentName?.trim();
                this.responses.update((messages) => [
                    ...messages,
                    {
                        kind: ChatMessageKind.HumanJoined,
                        text: name
                            ? ev.watchingOnly
                                ? `${name} is also here (viewing)`
                                : `${name} joined the chat`
                            : ev.watchingOnly
                              ? 'A team member is also here (viewing)'
                              : 'A team member joined the chat',
                        joinAgentName: name,
                        joinWatchingOnly: !!ev.watchingOnly,
                        ...(ev.agentImage?.trim() ? { joinAgentImageUrl: ev.agentImage.trim() } : {}),
                        fromAi: false,
                        fromHuman: false,
                    },
                ]);
                this.shouldScroll.set(true);
                this.maybePlayIncomingMessageSound();
                return;
            }

            if ('present' in ev && ev.present === false) {
                const showHandBack = untracked(
                    () => this.visitorHumanActive || this.isHumanAgentPresent(),
                );
                this.isHumanAgentPresent.set(false);
                this.activeStaffByIdentity.set({});
                this.nextAnonStaffKey = 0;
                if (showHandBack) {
                    this.visitorHumanActive = false;
                    this.responses.update((messages) => [
                        ...messages,
                        {
                            kind: ChatMessageKind.HumanLeft,
                            text: "You're back with the AI assistant.",
                            fromAi: false,
                            fromHuman: false,
                        },
                    ]);
                    this.shouldScroll.set(true);
                    this.maybePlayIncomingMessageSound();
                }
                return;
            }

            if ('present' in ev && ev.present) {
                this.isHumanAgentPresent.set(true);
                // Must not track activeStaffByIdentity: addStaffToActiveMap updates it; tracking would re-run
                // this effect forever with the same humanAgentPresent$ value (freeze on Enter chat).
                const mapBefore = untracked(() => this.activeStaffByIdentity());
                const id = ev.agentIdentity?.trim();
                const provisionalKey = id || `staff-${this.nextAnonStaffKey + 1}`;
                const isNewStaffMember = mapBefore[provisionalKey] === undefined;
                this.addStaffToActiveMap(ev.agentIdentity, ev.agentName);
                const name = ev.agentName?.trim();

                // Any takeover_start means AI is paused for the visitor — not only when we add a new join row
                // (spectator human_arrived already put this identity in the map).
                this.visitorHumanActive = true;

                if (isNewStaffMember) {
                    this.responses.update((messages) => [
                        ...messages,
                        {
                            kind: ChatMessageKind.HumanJoined,
                            text: name ? `${name} joined the chat` : 'A team member joined the chat',
                            joinAgentName: name,
                            joinWatchingOnly: false,
                            ...(ev.agentImage?.trim() ? { joinAgentImageUrl: ev.agentImage.trim() } : {}),
                            fromAi: false,
                            fromHuman: false,
                        },
                    ]);
                    this.shouldScroll.set(true);
                    this.maybePlayIncomingMessageSound();
                }
            }
        });

        // Effect to scroll when needed (new messages, etc.)
        effect(() => {
            if (this.shouldScroll()) {
                this.scheduleScrollChatToBottom();
                this.shouldScroll.set(false);
            }
        });

        // Agent builder: apply form-driven widget config without a parent iframe.
        effect(() => {
            if (!this.dashboardPreviewMode()) {
                return;
            }
            const cfg = this.dashboardPreviewConfig();
            if (!cfg || Object.keys(cfg).length === 0) {
                return;
            }
            this.widgetConfig.set(this.normalizeWidgetConfig({ ...cfg }));
            if (this.dashboardPreviewAutoOpen() && !this.dashboardPreviewAutoOpened) {
                this.isOpen.set(true);
                this.dashboardPreviewAutoOpened = true;
            }
            const behavior = this.dashboardPreviewBehavior();
            if (this.lastPreviewBehavior === 'style-only' && behavior === 'interactive-live') {
                this.resetInteractivePreviewState();
            }
            this.lastPreviewBehavior = behavior;
            if (behavior === 'style-only') {
                this.applyStyleOnlyPreviewState();
                return;
            }
            if (!this.dashboardPreviewPreChatGateDone) {
                this.showPreChatForm.set(true);
                this.dashboardPreviewPreChatGateDone = true;
            }
        });

        // Popup message: show after delay when collapsed, hide immediately when opened.
        let popupTimer: ReturnType<typeof setTimeout> | null = null;
        effect(() => {
            const open = this.isOpen();
            const cfg = this.widgetConfig()?.metaData;
            const enabled = cfg?.popupMessageEnabled;
            const delaySec = cfg?.popupDelaySeconds;
            const delayMs = (typeof delaySec === 'number' ? delaySec : Number(delaySec ?? 0)) * 1000;
            const validDelay = Number.isFinite(delayMs) && delayMs >= 0;

            if (popupTimer !== null) {
                clearTimeout(popupTimer);
                popupTimer = null;
            }

            if (open || !enabled) {
                this.popupVisible.set(false);
                return;
            }

            // Collapsed + enabled: show after delay (or immediately if 0 / invalid)
            if (!validDelay || delayMs === 0) {
                this.popupVisible.set(true);
            } else {
                popupTimer = setTimeout(() => {
                    popupTimer = null;
                    this.popupVisible.set(true);
                }, delayMs);
            }
        });
    }

    /**
     * Scroll the chat transcript to the latest message. Uses rAF + delayed flush so layout matches
     * after Angular renders (history batch, Connecting… → messages swap, iframe resize on reopen).
     */
    private scheduleScrollChatToBottom(): void {
        const run = () => {
            const container = this.chatScrollContainer?.nativeElement;
            if (!container) return;
            container.scrollTop = container.scrollHeight;
        };
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                run();
                setTimeout(run, 0);
                setTimeout(run, 50);
            });
        });
    }

    /**
     * Footer timestamp (stable per {@link sentAt}) — avoids NG0100 from relative "Xm ago" using Date.now() in the template.
     */
    formatMsgTime(ts?: number): string {
        if (ts == null) return '';
        const d = new Date(ts);
        if (Number.isNaN(d.getTime())) return '';
        const now = new Date();
        const sameDay =
            d.getFullYear() === now.getFullYear() &&
            d.getMonth() === now.getMonth() &&
            d.getDate() === now.getDate();
        if (sameDay) {
            return d.toLocaleTimeString(undefined, {
                hour: 'numeric',
                minute: '2-digit',
            });
        }
        return d.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    }

    private static readWidgetSoundPreferenceFromStorage(): boolean {
        try {
            if (typeof localStorage === 'undefined') {
                return true;
            }
            const v = localStorage.getItem(WidgetComponent.MESSAGE_SOUND_STORAGE_KEY);
            if (v === null) {
                return true;
            }
            return v !== '0' && v !== 'false';
        } catch {
            return true;
        }
    }

    toggleSoundNotifications(): void {
        const next = !this.soundNotificationsEnabled();
        this.soundNotificationsEnabled.set(next);
        try {
            localStorage.setItem(WidgetComponent.MESSAGE_SOUND_STORAGE_KEY, next ? '1' : '0');
        } catch {
            /* ignore quota / private mode */
        }
        if (next) {
            // Short audible preview on user gesture — unlocks autoplay for the same shared Audio element
            // (Chrome blocks unmuted playback until a gesture; silent play/pause was unreliable).
            this.playMessageSoundPreviewAfterUserGesture();
        }
    }

    /** Visitor: same notification sound for AI/human messages and team join/leave rows (when enabled). */
    private maybePlayIncomingMessageSound(): void {
        // Must not read signals here without untracked: callers include effect() that appends chat
        // messages; tracking e.g. soundNotificationsEnabled would re-run the effect on every bell toggle
        // and duplicate the last AI/human message.
        untracked(() => {
            if (!this.sessionStarted()) {
                return;
            }
            if (this.chatMode() === chatWidgetType.ChatWidgetAudio) {
                return;
            }
            if (!this.soundNotificationsEnabled()) {
                return;
            }
            this.playMessageSoundNow();
        });
    }

    private playMessageSoundNow(): void {
        if (this.messageSoundPreviewTimer != null) {
            clearTimeout(this.messageSoundPreviewTimer);
            this.messageSoundPreviewTimer = null;
        }
        const audio = this.ensureMessageSound();
        audio.muted = false;
        audio.volume = 1;
        audio.currentTime = 0;
        void audio.play().catch(() => {
            /* autoplay policy or missing asset */
        });
    }

    /** Brief preview so the browser unlocks playback; same element is reused for incoming messages. */
    private playMessageSoundPreviewAfterUserGesture(): void {
        if (this.messageSoundPreviewTimer != null) {
            clearTimeout(this.messageSoundPreviewTimer);
            this.messageSoundPreviewTimer = null;
        }
        const a = this.ensureMessageSound();
        this.messageSoundGesturePrimed = true;
        a.muted = false;
        a.volume = 0.35;
        a.currentTime = 0;
        void a.play().catch(() => {});
        this.messageSoundPreviewTimer = window.setTimeout(() => {
            this.messageSoundPreviewTimer = null;
            a.pause();
            a.currentTime = 0;
            a.volume = 1;
        }, 400);
    }

    private ensureMessageSound(): HTMLAudioElement {
        if (!this.messageSoundEl) {
            this.messageSoundEl = new Audio(WidgetComponent.MESSAGE_SOUND_URL);
            this.messageSoundEl.preload = 'auto';
        }
        return this.messageSoundEl;
    }

    /**
     * Browsers block unmuted audio until user gesture. Muted play + pause "warms" this element so a later
     * unmuted play() for incoming messages can succeed (toggle bell / open widget / send also help).
     */
    private primeMessageSoundAudioOnUserGesture(): void {
        if (this.messageSoundGesturePrimed) {
            return;
        }
        if (!this.soundNotificationsEnabled()) {
            return;
        }
        this.messageSoundGesturePrimed = true;
        const a = this.ensureMessageSound();
        a.muted = true;
        a.volume = 1;
        a.currentTime = 0;
        void a
            .play()
            .then(() => {
                a.pause();
                a.currentTime = 0;
                a.muted = false;
            })
            .catch(() => {
                a.muted = false;
            });
    }

    /**
     * Agent UUID for public routes (history, initiate-chatwidget-connection path param).
     * Prefer config from API/embed; else iframe query <code>?widgetUid=</code> (same as agent UUID).
     */
    private resolveAgentUuidForPublicApi(): string | undefined {
        const cfg = this.widgetConfig();
        const fromConfig = cfg?.agentUuid?.trim();
        if (fromConfig) return fromConfig;
        try {
            const q = new URLSearchParams(window.location.search).get('widgetUid');
            if (q?.trim()) return q.trim();
        } catch {
            /* ignore */
        }
        return undefined;
    }

    /** Same shape as dashboard / public API (metaData may arrive as JSON string). */
    private normalizeWidgetConfig(data: Record<string, unknown>): Widget {
        const out = { ...data } as unknown as Widget;
        let md: WidgetMetaData = {};
        const raw = out.metaData as unknown;
        if (typeof raw === 'string') {
            try {
                md = JSON.parse(raw) as WidgetMetaData;
            } catch {
                md = {};
            }
        } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
            md = this.normalizeWidgetMetaData(raw as Record<string, unknown>);
        }
        out.metaData = this.normalizeWidgetMetaData(md as Record<string, unknown>);
        // API returns `image` but the model and template use `imageUrl`
        if (!out.imageUrl && typeof data['image'] === 'string' && (data['image'] as string).trim()) {
            out.imageUrl = (data['image'] as string).trim();
        }
        return out;
    }

    /**
     * Canonical widget metadata used by widget UI.
     * Accepts legacy aliases for one compatibility cycle:
     * - `messageColor` -> `secondaryColor`
     * - `position` -> `widgetPosition`
     * - `shape` -> `widgetShape`
     */
    private normalizeWidgetMetaData(raw: Record<string, unknown>): WidgetMetaData {
        const footerHtml = this.coerceFooterHtml(raw);
        const md: WidgetMetaData = {
            welcomeScreenEnabled: this.coerceBoolean(raw['welcomeScreenEnabled'], true),
            welcomeDescription: this.coerceString(raw['welcomeDescription']),
            headerEnabled: this.coerceBoolean(raw['headerEnabled'], true),
            chatbotDisplayName: this.coerceString(raw['chatbotDisplayName']),
            messagePlaceholder: this.coerceString(raw['messagePlaceholder']),
            startChatPlaceholder: this.coerceString(raw['startChatPlaceholder']),
            borderRadius: this.coerceNumberOrString(raw['borderRadius']),
            primaryColor: this.coerceString(raw['primaryColor']),
            bubbleColor: this.coerceString(raw['bubbleColor']),
            secondaryColor: this.coerceString(raw['secondaryColor'] ?? raw['messageColor']),
            actionColor: this.coerceString(raw['actionColor']),
            widgetShape: this.coerceString(raw['widgetShape'] ?? raw['shape']),
            widgetPosition: this.coerceWidgetPosition(raw['widgetPosition'] ?? raw['position']),
            theme: this.coerceString(raw['theme']) as WidgetMetaData['theme'],
            widgetTheme: this.coerceString(raw['widgetTheme']) as WidgetMetaData['widgetTheme'],
            darkTheme: this.coerceBooleanLike(raw['darkTheme']),
            popupMessageEnabled: this.coerceBoolean(raw['popupMessageEnabled'], false),
            popupMessageText: this.coerceString(raw['popupMessageText']),
            popupDelaySeconds: this.coerceNumberOrString(raw['popupDelaySeconds']),
            footerHtml,
        };
        return md;
    }

    private coerceFooterHtml(raw: Record<string, unknown>): string | undefined {
        const footerHtml = this.coerceString(raw['footerHtml']);
        if (footerHtml) {
            return footerHtml;
        }
        // Backward compat: previous shape was `{ footer: { poweredBy, terms } }`.
        const legacyFooter = raw['footer'];
        if (!legacyFooter || typeof legacyFooter !== 'object' || Array.isArray(legacyFooter)) {
            return undefined;
        }
        const f = legacyFooter as Record<string, any>;
        const candidate = (f['terms'] ?? f['poweredBy']) as Record<string, unknown> | undefined;
        if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
            return undefined;
        }
        const legacy = candidate as Record<string, unknown> & { link?: { label?: unknown; url?: unknown } };
        const text = this.coerceString(legacy['text']) ?? '';
        const linkLabel = this.coerceString(legacy['linkLabel'] ?? legacy.link?.label) ?? '';
        const linkUrl = this.coerceString(legacy['linkUrl'] ?? legacy.link?.url) ?? '';
        const safeUrl = linkUrl.startsWith('http://') || linkUrl.startsWith('https://') ? linkUrl : '';
        const link = linkLabel && safeUrl ? ` <a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${linkLabel}</a>` : '';
        const out = `${text}${link}`.trim();
        return out || undefined;
    }

    footerHtml(): string {
        return (
            this.widgetConfig()?.metaData?.footerHtml
        );
    }

    private coerceString(v: unknown): string | undefined {
        return typeof v === 'string' && v.trim() ? v.trim() : undefined;
    }

    private coerceBoolean(v: unknown, fallback: boolean): boolean {
        const b = this.coerceBooleanLike(v);
        return typeof b === 'boolean' ? b : fallback;
    }

    private coerceBooleanLike(v: unknown): boolean | undefined {
        if (v === true || v === false) return v;
        if (v === 1 || v === '1' || v === 'true') return true;
        if (v === 0 || v === '0' || v === 'false') return false;
        return undefined;
    }

    private coerceNumberOrString(v: unknown): number | string | undefined {
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        if (typeof v === 'string' && v.trim()) return v.trim();
        return undefined;
    }

    private coerceWidgetPosition(v: unknown): WidgetPosition | undefined {
        const p = typeof v === 'string' ? v.trim() : '';
        if (p === 'top-left' || p === 'top-right' || p === 'bottom-left' || p === 'bottom-right') {
            return p;
        }
        return undefined;
    }

    metaPrimary(): string {
        const c = this.widgetConfig()?.metaData?.primaryColor;
        return typeof c === 'string' && c.trim() ? c.trim() : '#10b981';
    }

    metaBubbleColor(): string {
        const b = this.widgetConfig()?.metaData?.bubbleColor;
        if (typeof b === 'string' && b.trim()) return b.trim();
        return this.metaPrimary();
    }

    metaSecondary(): string {
        const c = this.widgetConfig()?.metaData?.secondaryColor;
        return typeof c === 'string' && c.trim() ? c.trim() : '#ffffff';
    }

    /**
     * Raw secondary color from config (no fallback), so we can apply it conditionally.
     */
    private configuredSecondaryColor(): string | null {
        const c = this.widgetConfig()?.metaData?.secondaryColor;
        return typeof c === 'string' && c.trim() ? c.trim() : null;
    }

    /**
     * Widget panel/body background color driven by branding secondary color.
     * Returns null when secondary is not configured to preserve existing theme defaults.
     */
    widgetBackgroundColor(): string | null {
        return this.configuredSecondaryColor();
    }

    /** Light UI by default; set metaData.theme or widgetTheme to <code>dark</code> for dark shell. */
    widgetDarkTheme(): boolean {
        const m = this.widgetConfig()?.metaData as Record<string, unknown> | undefined;
        if (!m) return false;
        const t = m['theme'] ?? m['widgetTheme'];
        if (typeof t === 'string' && t.toLowerCase().trim() === 'dark') return true;
        const d = m['darkTheme'];
        return d === true || d === 'true' || d === 1 || d === '1';
    }

    /** Title/headline: on light always dark text; on dark, secondary only if bright enough. */
    widgetHeadingTextColor(): string {
        if (!this.widgetDarkTheme()) return '#18181b';
        return this.readableOnDarkBackground(this.metaSecondary());
    }

    private readableOnDarkBackground(color: string): string {
        return this.colorRelativeLuminance(color) > 0.45 ? color : '#f4f4f5';
    }

    private colorRelativeLuminance(cssColor: string): number {
        const rgb = this.parseColorToRgb(cssColor);
        if (!rgb) return 0.2;
        const lin = (c: number) => {
            c /= 255;
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        };
        const r = lin(rgb.r),
            g = lin(rgb.g),
            b = lin(rgb.b);
        return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    }

    private parseColorToRgb(s: string): { r: number; g: number; b: number } | null {
        const t = s.trim();
        const hex = t.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (hex) {
            let h = hex[1]!;
            if (h.length === 3) {
                h = h[0]! + h[0]! + h[1]! + h[1]! + h[2]! + h[2]!;
            }
            return {
                r: parseInt(h.slice(0, 2), 16),
                g: parseInt(h.slice(2, 4), 16),
                b: parseInt(h.slice(4, 6), 16),
            };
        }
        const rgb = t.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
        if (rgb) {
            return { r: +rgb[1]!, g: +rgb[2]!, b: +rgb[3]! };
        }
        return null;
    }

    panelShellClass(): string {
        if (this.widgetMobileFullscreenOpen()) {
            const base =
                "flex flex-col w-full h-full min-h-0 flex-1 overflow-hidden rounded-none border-0";
            return this.widgetDarkTheme()
                ? `${base} bg-[#141414] text-neutral-100`
                : `${base} bg-white text-zinc-900`;
        }
        const base =
            'flex flex-col w-[min(440px,calc(100vw-1.25rem))] h-[var(--tg-widget-max-panel-height)] max-h-[var(--tg-widget-max-panel-height)] sm:w-[440px] sm:h-[min(680px,var(--tg-widget-max-panel-height))] sm:max-h-[var(--tg-widget-max-panel-height)] mb-2 overflow-hidden rounded-[20px]';
        return this.widgetDarkTheme()
            ? `${base} border border-white/10 bg-[#141414] text-neutral-100`
            : `${base} border border-zinc-200 bg-white text-zinc-900`;
    }

    mainColumnClass(): string {
        return this.widgetDarkTheme()
            ? 'flex-1 min-h-0 flex flex-col overflow-hidden bg-[#141414]'
            : 'flex-1 min-h-0 flex flex-col overflow-hidden bg-zinc-50 rounded-2xl';
    }

    /** Flat corners when the iframe is mobile fullscreen; otherwise widget-config radius. */
    panelBorderRadiusPx(): number {
        if (this.widgetMobileFullscreenOpen()) {
            return 0;
        }
        return this.borderRadiusPx();
    }

    /** Extra top inset for chrome inside the panel (standalone widget page only; embed uses host padding). */
    widgetTopChromeInsetClass(): string {
        if (!this.isMobileWidgetViewport() || !this.isOpen() || this.isEmbeddedWidget) {
            return '';
        }
        return 'pt-[max(0.75rem,env(safe-area-inset-top,0px))]';
    }

    /** Host positioning: corner bubble vs full-bleed when iframe is expanded on small viewports. */
    widgetHostRootNgClass(): string {
        if (this.dashboardPreviewMode()) {
            const pos =
                this.widgetConfig()?.metaData?.widgetPosition ?? this.position();
            const align =
                pos === 'bottom-left' || pos === 'top-left'
                    ? 'items-start'
                    : 'items-end';
            const verticalAlign = this.dashboardPreviewVerticalAlign() === 'center'
                ? 'justify-center'
                : 'justify-end';
            return `relative z-10 flex flex-col w-full h-full min-h-[480px] max-h-[720px] ${verticalAlign} ${align} box-border overflow-hidden`;
        }
        if (this.widgetMobileFullscreenOpen()) {
            const safePad =
                this.isEmbeddedWidget
                    ? ''
                    : ' pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)] pl-[env(safe-area-inset-left,0px)] pr-[env(safe-area-inset-right,0px)]';
            return `fixed inset-0 z-[9998] flex flex-col w-full h-full min-h-0 max-h-[100dvh] items-stretch overflow-hidden box-border${safePad}`;
        }
        const pos = this.widgetConfig()?.metaData?.widgetPosition ?? this.position();
        const base = "fixed z-[9998] flex flex-col max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-1.25rem)]";
        const open = this.isOpen();
        if (!open) {
            return `fixed inset-0 z-[9998] block h-full w-full min-h-0 min-w-0 overflow-hidden`;
        }
        switch (pos) {
            case "bottom-left":
                return `${base} bottom-[max(1.25rem,env(safe-area-inset-bottom,0px))] left-[max(1.25rem,env(safe-area-inset-left,0px))] items-start`;
            case "top-right":
                return `${base} top-[max(1.25rem,env(safe-area-inset-top,0px))] right-[max(1.25rem,env(safe-area-inset-right,0px))] items-end`;
            case "top-left":
                return `${base} top-[max(1.25rem,env(safe-area-inset-top,0px))] left-[max(1.25rem,env(safe-area-inset-left,0px))] items-start`;
            case "bottom-right":
            default:
                return `${base} bottom-[max(1.25rem,env(safe-area-inset-bottom,0px))] right-[max(1.25rem,env(safe-area-inset-right,0px))] items-end`;
        }
    }

    chatScrollAreaClass(): string {
        return this.widgetDarkTheme()
            ? 'flex-1 overflow-y-auto tg-scroll-dark px-4 py-4 bg-[#141414] [background-image:radial-gradient(circle,#ffffff08_1px,transparent_1px)] [background-size:22px_22px]'
            : 'flex-1 overflow-y-auto tg-scroll-light px-4 py-4 bg-zinc-50 [background-image:radial-gradient(circle,#00000009_1px,transparent_1px)] [background-size:22px_22px]';
    }

    metaAction(): string {
        const c = this.widgetConfig()?.metaData?.actionColor;
        return typeof c === 'string' && c.trim() ? c.trim() : this.metaPrimary();
    }

    /** Widget title → description → meta welcomeDescription (API order). */
    welcomeHeadline(): string {
        const t = this.widgetConfig()?.title?.trim();
        return t;
    }

    welcomeSubcopy(): string {
            const d = this.widgetConfig()?.description?.trim();
            return d;
    }

    /** In-chat top bar; default on. */
    chatHeaderEnabled(): boolean {
        const v = this.widgetConfig()?.metaData?.headerEnabled as unknown;
        if (v === false || v === 'false' || v === 0 || v === '0') return false;
        return true;
    }

    /** Widget setting: skip pre-chat form; parent embed omits email so the server creates an anonymous guest. */
    isAnonymousChatEnabled(): boolean {
        const cfg = this.widgetConfig() as unknown as { enableAnonymousChat?: unknown } | null;
        if (!cfg) return false;
        const v = cfg.enableAnonymousChat;
        return v === true || v === 'true' || v === 1 || v === '1';
    }

    borderRadiusPx(): number {
        const b = this.widgetConfig()?.metaData?.borderRadius;
        const n = typeof b === 'string' ? parseInt(b, 10) : Number(b);
        return Number.isFinite(n) && n >= 0 ? n : 20;
    }

    /**
     * Launcher shape from widget config (`Circle`/`Square`).
     */
    launcherBorderRadiusCss(): string {
        const shape = this.widgetConfig()?.metaData?.widgetShape;
        if (typeof shape === 'string' && shape.trim().toLowerCase() === 'square') {
            return '10px';
        }
        return '9999px';
    }

    private formatHumanTypingNames(names: string[]): string {
        const uniq = [...new Set(names.map((n) => n.trim()).filter(Boolean))];
        if (uniq.length === 0) return 'Team member';
        if (uniq.length === 1) return uniq[0]!;
        if (uniq.length === 2) return `${uniq[0]} and ${uniq[1]}`;
        return `${uniq[0]} and ${uniq.length - 1} other${uniq.length > 2 ? 's' : ''}`;
    }

    private typingParticipantDisplayLabel(
        participant: RemoteTypingParticipant,
        source: 'enduser' | 'human' | 'agent',
    ): string {
        const name = participant.name?.trim();
        if (name) return name;
        const id = participant.identity?.trim();
        if (id && id !== '__anon') return id;
        if (source === 'agent') return 'Assistant';
        return 'Someone';
    }

    ngOnInit(): void {
        // Best-effort: default the phone country to the visitor's location (self-guarded).
        void this.detectAndApplyGeoCountry();

        this.callSvc.onEndCall.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (this.isStyleOnlyDashboardPreview()) {
                return;
            }
            this.endSession();
        });
        this.callSvc.onRemoteTyping.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((e) => {
            if (e.source === 'human') {
                const id = (e.participant.identity?.trim() || '__anon').trim();
                const label = this.typingParticipantDisplayLabel(e.participant, 'human');
                this.humanAgentsTypingByIdentity.update((rec) => {
                    const next = { ...rec };
                    if (e.isTyping) {
                        next[id] = label;
                    } else {
                        delete next[id];
                    }
                    return next;
                });
                if (e.isTyping) this.scheduleScrollChatToBottom();
                return;
            }
            if (e.source === 'agent') {
                this.aiAssistantTyping.set(e.isTyping);
                if (!e.isTyping) {
                    this.agentTypingPhase.set(null);
                    this.agentTypingHint.set(null);
                    return;
                }
                if (e.phase === 'tools') {
                    this.agentTypingPhase.set('tools');
                    this.agentTypingHint.set(e.hint?.trim() || null);
                } else {
                    this.agentTypingPhase.set('generating');
                    this.agentTypingHint.set(null);
                }
                this.scheduleScrollChatToBottom();
            }
        });
        // Note: Using '*' for target origin as this is a widget that needs to communicate with parent
        // In production, consider restricting to specific origins
        window.parent.postMessage("widget:componentLoaded", "*");

        window.addEventListener('message', (event: MessageEvent) => {
            // Note: In production, verify event.origin matches expected parent origin
            const iframeEvent: IframeEvent = event.data;
            
            if (iframeEvent.type === 'widget:setOpen') {
                const payload = iframeEvent.data as { open?: boolean } | undefined;
                if (typeof payload?.open === 'boolean') {
                    this.applyOpenState(payload.open, false);
                }
            } else if (iframeEvent.type === 'widgetConfig') {
                const config = this.normalizeWidgetConfig(
                    iframeEvent.data as Record<string, unknown>,
                );
                this.widgetConfig.set(config);

                const stored = config.storedContact;
                const legacyEmail = config.storedEmail;
                if (stored?.email?.trim()) {
                    const fn = (stored.firstName ?? '').trim();
                    const ln = (stored.lastName ?? '').trim();
                    this.preChatFullName.set([fn, ln].filter(Boolean).join(' ').trim());
                    this.preChatEmail.set(stored.email.trim());
                    this.parseStoredPhoneIntoDialAndLocal((stored.phoneNumber ?? '').trim());
                    this.showPreChatForm.set(false);
                    this.validatePreChat();
                } else if (legacyEmail?.trim()) {
                    this.preChatEmail.set(legacyEmail.trim());
                    this.showPreChatForm.set(true);
                    this.validatePreChat();
                } else if (this.isAnonymousChatEnabled()) {
                    this.showPreChatForm.set(false);
                    this.isPreChatValid.set(true);
                    this.preChatError.set('');
                } else {
                    this.showPreChatForm.set(true);
                }
            } else if (iframeEvent.type === 'callStarted') {
                this.isHttpRequestInProcess.set(false);
                const data: {
                    wsUrl: string;
                    token: string;
                    chatMode?: string;
                    attachmentUploadToken?: string;
                    roomName?: string;
                    liveKitRoomSids?: string;
                } = iframeEvent.data;
                void this.onSessionStarted(data);
            } else if (iframeEvent.type === 'widget:attachmentUploadUrlResult') {
                const envelope = event.data as {
                    requestId?: string;
                    data?: { uploadUrl?: string; publicUrl?: string } | null;
                    error?: string;
                };
                const requestId = envelope.requestId?.trim();
                if (!requestId) {
                    return;
                }
                const pending = this.pendingAttachmentUploadRequests.get(requestId);
                if (!pending) {
                    return;
                }
                this.pendingAttachmentUploadRequests.delete(requestId);
                clearTimeout(pending.timeoutId);
                if (envelope.error?.trim()) {
                    pending.reject(new Error(envelope.error.trim()));
                    return;
                }
                const uploadUrl = String(envelope.data?.uploadUrl ?? '').trim();
                const publicUrl = String(envelope.data?.publicUrl ?? '').trim();
                if (!uploadUrl) {
                    pending.reject(new Error('No uploadUrl in parent response'));
                    return;
                }
                pending.resolve({ uploadUrl, publicUrl });
            } else if (iframeEvent.type === 'widget:recentConversationsResult') {
                const envelope = event.data as {
                    requestId?: string;
                    data?: {
                        conversations?: {
                            callSid: string;
                            callType: string;
                            startedAt: string | null;
                            messages: { role: string; message: string; senderName?: string }[];
                        }[];
                    } | null;
                    error?: string;
                };
                const requestId = envelope.requestId?.trim();
                if (!requestId) {
                    return;
                }
                const pending = this.pendingRecentConversationsRequests.get(requestId);
                if (!pending) {
                    return;
                }
                this.pendingRecentConversationsRequests.delete(requestId);
                clearTimeout(pending.timeoutId);
                if (envelope.error?.trim()) {
                    pending.reject(new Error(envelope.error.trim()));
                    return;
                }
                pending.resolve(envelope.data?.conversations ?? []);
            } else if (iframeEvent.type === 'widget:feedbackResult') {
                const envelope = event.data as {
                    requestId?: string;
                    data?: { ok?: boolean } | null;
                    error?: string;
                };
                const requestId = envelope.requestId?.trim();
                if (!requestId) {
                    return;
                }
                const pending = this.pendingFeedbackRequests.get(requestId);
                if (!pending) {
                    return;
                }
                this.pendingFeedbackRequests.delete(requestId);
                clearTimeout(pending.timeoutId);
                if (envelope.error?.trim()) {
                    pending.reject(new Error(envelope.error.trim()));
                    return;
                }
                pending.resolve();
            }
        });
    }

    /** Embed host (talkgeniejs) must proxy public API calls so Origin matches the customer site. */
    private useWidgetParentApiProxy(): boolean {
        return !this.dashboardPreviewMode() && typeof window !== 'undefined' && window.parent !== window;
    }

    /**
     * @param notifyParent When false, only updates iframe UI (host page drove open/close via talkgeniejs).
     */
    private applyOpenState(open: boolean, notifyParent = true): void {
        if (this.isOpen() === open) {
            return;
        }
        this.showHeaderMenu.set(false);
        this.isOpen.set(open);
        if (open) {
            this.unreadWhileMinimized.set(0);
            this.postUnreadCountToParent(0);
            if (this.chatMode() !== chatWidgetType.ChatWidgetAudio) {
                queueMicrotask(() => this.primeMessageSoundAudioOnUserGesture());
            }
            queueMicrotask(() => this.scheduleScrollChatToBottom());
            setTimeout(() => this.scheduleScrollChatToBottom(), 120);
        }
        if (notifyParent) {
            // Note: Using '*' for target origin as this is a widget that needs to communicate with parent
            window.parent.postMessage('widget:toggleSize', '*');
        }
    }

    toggleChat(): void {
        this.applyOpenState(!this.isOpen());
    }

    toggleHeaderMenu(ev: Event): void {
        ev.stopPropagation();
        this.showHeaderMenu.update((v) => !v);
    }

    closeHeaderMenu(): void {
        this.showHeaderMenu.set(false);
    }

    endSessionFromMenu(): void {
        this.showHeaderMenu.set(false);
        this.endSession();
    }

    /** Bump unread when AI (done) or human agent sends a message and the panel is closed. */
    private noteIncomingAssistantMessageWhileMinimized(): void {
        // Entire side effect must stay in untracked: reading unreadWhileMinimized() after update would
        // subscribe the chat stream effect to that signal and re-run it on every bump → count explodes to 99.
        untracked(() => {
            if (this.isOpen()) return;
            if (!this.sessionStarted()) return;
            if (this.chatMode() === chatWidgetType.ChatWidgetAudio) return;
            let next = 0;
            this.unreadWhileMinimized.update((n) => {
                next = Math.min(n + 1, 99);
                return next;
            });
            this.postUnreadCountToParent(next);
        });
    }

    private postUnreadCountToParent(count: number): void {
        try {
            window.parent.postMessage({ type: 'widget:unreadCount', count }, '*');
        } catch {
            /* ignore */
        }
    }

    unreadBadgeLabel(): string {
        const n = this.unreadWhileMinimized();
        if (n > 99) return '99+';
        return String(n);
    }
    
    private clearVisitorTypingOutbound(): void {
        if (this.visitorTypingIdleTimer) {
            clearTimeout(this.visitorTypingIdleTimer);
            this.visitorTypingIdleTimer = null;
        }
        if (this.visitorTypingOutbound) {
            this.callSvc.publishTypingStatus(false);
            this.visitorTypingOutbound = false;
        }
    }

    /** Chat input: debounced typing pings to the room (human agent sees via {@link CallService.onRemoteTyping}). */
    onVisitorMessageInput(value: string): void {
        this.userTextMessage.set(value);
        if (!this.sessionStarted()) return;
        const hasText = value.trim().length > 0;
        if (hasText) {
            if (!this.visitorTypingOutbound) {
                this.callSvc.publishTypingStatus(true);
                this.visitorTypingOutbound = true;
            }
            if (this.visitorTypingIdleTimer) clearTimeout(this.visitorTypingIdleTimer);
            this.visitorTypingIdleTimer = setTimeout(() => {
                this.visitorTypingIdleTimer = null;
                this.visitorTypingOutbound = false;
                this.callSvc.publishTypingStatus(false);
            }, 2500);
        } else {
            this.clearVisitorTypingOutbound();
        }
    }

    sendMessage(): void {
        if (this.attachmentUploading()) return;
        this.clearVisitorTypingOutbound();

        // WhatsApp-style: image preview + caption → one combined send (Enter or main send button).
        if (this.visitorAttachmentPreviewFile()) {
            void this.sendVisitorAttachmentPreview();
            return;
        }

        const message = this.userTextMessage().trim();
        if (!message) return;

        if (this.isStyleOnlyDashboardPreview()) {
            const t = Date.now();
            this.responses.update((messages) => [
                ...messages,
                { text: message, fromAi: false, fromHuman: false, sentAt: t },
                {
                    text: 'Preview response: styling-only mode (no live session).',
                    fromAi: true,
                    fromHuman: false,
                    sentAt: t + 1,
                },
            ]);
            this.userTextMessage.set('');
            this.shouldScroll.set(true);
            return;
        }

        this.primeMessageSoundAudioOnUserGesture();

        // lk.chat → agent session → conversation_item_added → one TranscriptionMessage to broker.
        // human_chat → worker → second TranscriptionMessage (duplicate). Send only one path:
        // human takeover: human_chat only (lk.chat is paused on worker); AI chat: lk.chat only.
        if (this.isHumanAgentPresent()) {
            this.callSvc.sendMessage.next({ text: message, channel: 'human_chat' });
        } else {
            this.callSvc.sendMessage.next({ text: message, channel: 'lk.chat' });
        }
        const t = Date.now();
        this.responses.update((messages) => [
            ...messages,
            { text: message, fromAi: false, fromHuman: false, sentAt: t },
        ]);
        this.userTextMessage.set('');
        this.shouldScroll.set(true);
    }

    openVisitorAttachmentPicker(): void {
        if (!this.sessionStarted() || this.attachmentUploading()) {
            return;
        }
        this.attachmentError.set('');
        this.visitorAttachmentInput?.nativeElement?.click();
    }

    /** lk.chat payload: plain GCS image URL, or JSON with optional caption (worker ImageContent). */
    private buildImageChatPayload(publicUrl: string, caption?: string): string {
        const cap = caption?.trim();
        if (cap) {
            return JSON.stringify({ tg: 'img_v1', url: publicUrl, caption: cap });
        }
        return publicUrl;
    }

    /**
     * Signed URL from public widget-chat-attachment-upload-url, then PUT file bytes; message text
     * is the public HTTPS URL for transcript / broker.
     *
     * In embed mode the signed-URL POST is proxied via talkgeniejs on the parent page so the
     * browser Origin matches the host site, not the widget iframe domain.
     */
    private parseAttachmentUploadInitResponse(
        json: Record<string, unknown>,
    ): { uploadUrl: string; publicUrl: string } {
        const raw =
            (json['data'] && typeof json['data'] === 'object'
                ? (json['data'] as Record<string, unknown>)
                : json) ?? json;
        const uploadUrl = String(raw['uploadUrl'] ?? '').trim();
        const publicUrl = String(raw['publicUrl'] ?? '').trim();
        if (!uploadUrl) {
            throw new Error('No uploadUrl in API response');
        }
        return { uploadUrl, publicUrl };
    }

    /** Dashboard preview only — no talkgeniejs parent to proxy Origin. */
    private async fetchAttachmentUploadUrlDirect(
        agentUuid: string,
        uploadAuth: string,
        fileName: string,
        contentType: string,
    ): Promise<{ uploadUrl: string; publicUrl: string }> {
        const initRes = await fetch(
            `${environment.apiUrl}/public/widget-chat-attachment-upload-url/${encodeURIComponent(agentUuid)}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${uploadAuth}`,
                },
                body: JSON.stringify({ fileName, contentType }),
            },
        );
        if (!initRes.ok) {
            const errText = await initRes.text().catch(() => '');
            throw new Error(errText || `HTTP ${initRes.status}`);
        }
        const json = (await initRes.json()) as Record<string, unknown>;
        return this.parseAttachmentUploadInitResponse(json);
    }

    private requestAttachmentUploadUrlViaParent(
        agentUuid: string,
        uploadAuth: string,
        fileName: string,
        contentType: string,
    ): Promise<{ uploadUrl: string; publicUrl: string }> {
        return new Promise((resolve, reject) => {
            const requestId = crypto.randomUUID();
            const timeoutId = setTimeout(() => {
                this.pendingAttachmentUploadRequests.delete(requestId);
                reject(new Error('Attachment upload request timed out'));
            }, 30_000);
            this.pendingAttachmentUploadRequests.set(requestId, {
                resolve,
                reject,
                timeoutId,
            });
            window.parent.postMessage(
                {
                    type: 'widget:requestAttachmentUploadUrl',
                    requestId,
                    data: { agentUuid, fileName, contentType, uploadAuth },
                },
                '*',
            );
        });
    }

    private async getWidgetAttachmentUploadUrl(
        agentUuid: string,
        uploadAuth: string,
        fileName: string,
        contentType: string,
    ): Promise<{ uploadUrl: string; publicUrl: string }> {
        if (this.useWidgetParentApiProxy()) {
            return this.requestAttachmentUploadUrlViaParent(
                agentUuid,
                uploadAuth,
                fileName,
                contentType,
            );
        }
        return this.fetchAttachmentUploadUrlDirect(
            agentUuid,
            uploadAuth,
            fileName,
            contentType,
        );
    }

    private async fetchRecentConversationsDirect(
        agentUuid: string,
        email: string,
        uploadAuth: string,
    ): Promise<
        {
            callSid: string;
            callType: string;
            startedAt: string | null;
            messages: { role: string; message: string; senderName?: string }[];
        }[]
    > {
        const url = `${environment.apiUrl}/public/widget-recent-conversations/${encodeURIComponent(agentUuid)}?email=${encodeURIComponent(email)}`;
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${uploadAuth}` },
        });
        if (!res.ok) {
            return [];
        }
        const body = (await res.json()) as {
            data?: {
                conversations?: {
                    callSid: string;
                    callType: string;
                    startedAt: string | null;
                    messages: { role: string; message: string; senderName?: string }[];
                }[];
            };
            conversations?: {
                callSid: string;
                callType: string;
                startedAt: string | null;
                messages: { role: string; message: string; senderName?: string }[];
            }[];
        };
        return body.data?.conversations ?? body.conversations ?? [];
    }

    private requestRecentConversationsViaParent(
        agentUuid: string,
        email: string,
        uploadAuth: string,
    ): Promise<
        {
            callSid: string;
            callType: string;
            startedAt: string | null;
            messages: { role: string; message: string; senderName?: string }[];
        }[]
    > {
        return new Promise((resolve, reject) => {
            const requestId = crypto.randomUUID();
            const timeoutId = setTimeout(() => {
                this.pendingRecentConversationsRequests.delete(requestId);
                reject(new Error('Recent conversations request timed out'));
            }, 30_000);
            this.pendingRecentConversationsRequests.set(requestId, {
                resolve,
                reject,
                timeoutId,
            });
            window.parent.postMessage(
                {
                    type: 'widget:requestRecentConversations',
                    requestId,
                    data: { agentUuid, email, uploadAuth },
                },
                '*',
            );
        });
    }

    private async loadRecentConversationHistory(
        agentUuid: string,
        email: string,
        uploadAuth: string,
    ): Promise<Message[]> {
        try {
            const conversations = this.useWidgetParentApiProxy()
                ? await this.requestRecentConversationsViaParent(agentUuid, email, uploadAuth)
                : await this.fetchRecentConversationsDirect(agentUuid, email, uploadAuth);
            return this.messagesFromRecentConversations(conversations);
        } catch {
            return [];
        }
    }

    private submitFeedbackViaParent(
        agentUuid: string,
        uploadAuth: string,
        callSid: string,
        rating: number,
        isResolved: boolean,
        comment?: string,
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            const requestId = crypto.randomUUID();
            const timeoutId = setTimeout(() => {
                this.pendingFeedbackRequests.delete(requestId);
                reject(new Error('Feedback request timed out'));
            }, 30_000);
            this.pendingFeedbackRequests.set(requestId, {
                resolve,
                reject,
                timeoutId,
            });
            window.parent.postMessage(
                {
                    type: 'widget:submitFeedback',
                    requestId,
                    data: {
                        agentUuid,
                        uploadAuth,
                        callSid,
                        rating,
                        isResolved,
                        comment,
                    },
                },
                '*',
            );
        });
    }

    /** Dashboard preview only — no talkgeniejs parent to proxy Origin. */
    private async submitFeedbackDirect(
        agentUuid: string,
        uploadAuth: string,
        callSid: string,
        rating: number,
        isResolved: boolean,
        comment?: string,
    ): Promise<void> {
        await fetch(
            `${environment.apiUrl}/public/widget-feedback/${encodeURIComponent(agentUuid)}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${uploadAuth}`,
                },
                body: JSON.stringify({
                    callSid,
                    rating,
                    isResolved,
                    comment: comment?.trim() || undefined,
                }),
            },
        );
    }

    async onVisitorAttachmentSelected(ev: Event): Promise<void> {
        const input = ev.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) return;
        this.processVisitorAttachmentFile(file);
    }

    processVisitorAttachmentFile(file: File): void {
        if (!this.sessionStarted() || this.attachmentUploading()) {
            return;
        }
        const agentUuid = this.resolveAgentUuidForPublicApi();
        if (!agentUuid) {
            this.attachmentError.set('Missing agent configuration');
            return;
        }
        const maxBytes = 15 * 1024 * 1024;
        if (file.size > maxBytes) {
            this.attachmentError.set('File is too large (max 15 MB)');
            return;
        }
        if (!this.isHumanAgentPresent() && !this.isImageMimeOrFileName(file.type, file.name)) {
            this.attachmentError.set('Only image files are supported with the AI assistant.');
            return;
        }
        this.attachmentError.set('');
        this.beginVisitorAttachmentPreview(file);
    }

    onVisitorPaste(event: ClipboardEvent): void {
        const items = event.clipboardData?.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.kind === 'file' && item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (file) {
                    event.preventDefault();
                    this.processVisitorAttachmentFile(file);
                    break;
                }
            }
        }
    }

    cancelVisitorAttachmentPreview(): void {
        const url = this.visitorAttachmentPreviewUrl();
        if (url) URL.revokeObjectURL(url);
        this.visitorAttachmentPreviewUrl.set(null);
        this.visitorAttachmentPreviewFile.set(null);
        this.visitorAttachmentPreviewIsImage.set(false);
        this.visitorAttachmentPreviewIsVideo.set(false);
     }

     async sendVisitorAttachmentPreview(): Promise<void> {
         const file = this.visitorAttachmentPreviewFile();
         if (!file || this.attachmentUploading()) return;
         if (!this.sessionStarted()) return;

         const agentUuid = this.resolveAgentUuidForPublicApi();
         if (!agentUuid) {
             this.attachmentError.set('Missing agent configuration');
             return;
         }

         this.attachmentError.set('');
         this.attachmentUploading.set(true);
         this.primeMessageSoundAudioOnUserGesture();

         const contentType = (file.type && file.type.trim()) || 'application/octet-stream';
         const isImage = this.visitorAttachmentPreviewIsImage();
         const caption = this.userTextMessage().trim();
         const channel = this.isHumanAgentPresent() ? 'human_chat' : 'lk.chat';
         const localUrl = this.visitorAttachmentPreviewUrl();

         try {
             const uploadAuth = this.widgetAttachmentUploadToken?.trim();
             if (!uploadAuth) {
                 this.attachmentError.set('Session expired. Please refresh and start chat again to attach files.');
                 return;
             }
             const { uploadUrl, publicUrl: publicUrlResp } =
                 await this.getWidgetAttachmentUploadUrl(
                     agentUuid,
                     uploadAuth,
                     file.name,
                     contentType,
                 );

             const putRes = await fetch(uploadUrl, {
                 method: 'PUT',
                 headers: { 'Content-Type': contentType },
                 body: file,
             });
             if (!putRes.ok) {
                 throw new Error(`Upload failed (${putRes.status})`);
             }

             const publicUrl = publicUrlResp;
             if (!publicUrl) {
                 throw new Error('No public URL returned');
             }

             const chatPayload =
                 channel === 'lk.chat' && isImage
                     ? this.buildImageChatPayload(publicUrl, caption)
                     : caption
                       ? `${publicUrl}\n${caption}`
                       : publicUrl;

             this.cancelVisitorAttachmentPreview();
            this.userTextMessage.set('');
            this.callSvc.sendMessage.next({ text: chatPayload, channel });
            const t = Date.now();
            const displayText = caption ? `${publicUrl}\n${caption}` : publicUrl;
            this.responses.update((messages) => [
                ...messages,
                {
                    text: displayText,
                    fromAi: false,
                    fromHuman: false,
                    sentAt: t,
                    attachment: { url: publicUrl, fileName: file.name, isImage },
                },
            ]);
            this.shouldScroll.set(true);
        } catch (e) {
            console.error('Widget attachment upload failed', e);
            this.attachmentError.set('Could not upload file. Please try again.');
        } finally {
            this.attachmentUploading.set(false);
        }
    }

    private beginVisitorAttachmentPreview(file: File): void {
        this.cancelVisitorAttachmentPreview();
        this.cancelVoicePreview();
        const isImage = this.isImageMimeOrFileName(file.type, file.name);
        const isVideo = this.isVideoMimeOrFileName(file.type, file.name);
        this.visitorAttachmentPreviewFile.set(file);
        this.visitorAttachmentPreviewIsImage.set(isImage);
        this.visitorAttachmentPreviewIsVideo.set(isVideo);
        if (isImage || isVideo) {
            this.visitorAttachmentPreviewUrl.set(URL.createObjectURL(file));
        }
    }

    private isVideoMimeOrFileName(mime: string, fileName: string): boolean {
        const m = (mime || '').toLowerCase();
        if (m.startsWith('video/')) return true;
        return /\.(mp4|m4v|mov|webm|ogv|3gp)$/i.test(fileName);
    }

    /** Toggle voice recording; works in AI chat (`lk.chat`) and when a human is present (`human_chat` after upload). */
    async toggleVoiceRecord(): Promise<void> {
        if (this.isVoiceRecording()) {
            this._stopVoiceRecording();
        } else {
            await this._startVoiceRecording();
        }
    }

    private async _startVoiceRecording(): Promise<void> {
        if (!this.sessionStarted()) return;
        this.voiceError.set('');
        this.cancelVisitorAttachmentPreview();
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.audioChunks = [];
            const options = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? { mimeType: 'audio/webm;codecs=opus' }
                : MediaRecorder.isTypeSupported('audio/webm')
                    ? { mimeType: 'audio/webm' }
                    : {};
            this.mediaRecorder = new MediaRecorder(stream, options);
            this.mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) this.audioChunks.push(e.data);
            };
            this.mediaRecorder.onstop = () => {
                stream.getTracks().forEach((t) => t.stop());
                const durationSec = this.voiceRecordingSeconds();
                if (this.audioChunks.length > 0) {
                    const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
                    const blob = new Blob(this.audioChunks, { type: mimeType });
                    this.audioChunks = [];
                    const prev = this.voicePreviewBlobUrl();
                    if (prev) URL.revokeObjectURL(prev);
                    this.voicePreviewBlobUrl.set(URL.createObjectURL(blob));
                    this.voicePreviewBlob.set(blob);
                    this.voicePreviewDurationSec.set(durationSec);
                }
            };
            this.mediaRecorder.start(250);
            this.voiceRecordingSeconds.set(0);
            this.isVoiceRecording.set(true);
            this.recordingTimerRef = setInterval(() => {
                this.voiceRecordingSeconds.update((s) => s + 1);
                // Auto-stop at 60 s
                if (this.voiceRecordingSeconds() >= 60) {
                    this._stopVoiceRecording();
                }
            }, 1000);
        } catch {
            this.voiceError.set('Microphone access denied.');
        }
    }

    private _stopVoiceRecording(): void {
        if (this.recordingTimerRef) {
            clearInterval(this.recordingTimerRef);
            this.recordingTimerRef = null;
        }
        this.isVoiceRecording.set(false);
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.stop();
        }
    }

    /** Discard the recorded preview and return to normal input. */
    cancelVoicePreview(): void {
        const url = this.voicePreviewBlobUrl();
        // Clear signals first so Angular removes the <audio> element from the DOM.
        // Revoke the object URL only after a short delay so the browser can clean up
        // the audio stream gracefully (avoids audible pop/glitch on abrupt termination).
        this.voicePreviewBlobUrl.set(null);
        this.voicePreviewBlob.set(null);
        this.voicePreviewDurationSec.set(0);
        this.voiceError.set('');
        if (url) setTimeout(() => URL.revokeObjectURL(url), 500);
    }

    /** Upload and send the previewed voice recording. */
    async sendVoiceMessage(): Promise<void> {
        const blob = this.voicePreviewBlob();
        const previewUrl = this.voicePreviewBlobUrl();
        // Guard against double-tap while uploading
        if (!blob || this.voiceProcessing()) return;
        const durationSec = this.voicePreviewDurationSec();

        // Clear the blob reference to prevent double-send, but keep voicePreviewBlobUrl so
        // the preview block stays visible — the send button will show its built-in spinner.
        this.voicePreviewBlob.set(null);

        await this._uploadAndTranscribeVoice(blob, durationSec);

        // Upload finished — now remove the preview UI and revoke the object URL.
        this.voicePreviewBlobUrl.set(null);
        this.voicePreviewDurationSec.set(0);
        if (previewUrl) setTimeout(() => URL.revokeObjectURL(previewUrl), 500);
    }

    private async _uploadAndTranscribeVoice(blob: Blob, durationSec: number): Promise<void> {
        const agentUuid = this.resolveAgentUuidForPublicApi();
        const uploadAuth = this.widgetAttachmentUploadToken?.trim();
        if (!agentUuid || !uploadAuth) return;

        this.voiceProcessing.set(true);
        this.voiceError.set('');

        try {
            const mimeType = blob.type || 'audio/webm';
            const ext = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'mp4' : 'webm';
            const fileName = `voice-${Date.now()}.${ext}`;

            const { uploadUrl, publicUrl } = await this.getWidgetAttachmentUploadUrl(
                agentUuid,
                uploadAuth,
                fileName,
                mimeType,
            );
            if (!publicUrl) {
                throw new Error('No public URL returned');
            }

            // 2. PUT audio blob to GCS
            const putRes = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': mimeType },
                body: blob,
            });
            if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);

            // 3. Send the GCS URL as a chat message.
            //    For AI chat: the LiveKit worker detects the audio URL, runs STT, then replies.
            //    For human chat: the URL renders as an audio player in both sides of the chat.
            const channel = this.isHumanAgentPresent() ? 'human_chat' : 'lk.chat';
            this.callSvc.sendMessage.next({ text: publicUrl, channel });

            // 4. Optimistically show the voice message in local chat with an audio player
            const blobUrl = URL.createObjectURL(blob);
            const t = Date.now();
            this.responses.update((msgs) => [
                ...msgs,
                {
                    text: publicUrl,
                    fromAi: false,
                    fromHuman: false,
                    sentAt: t,
                    voiceAttachment: { url: publicUrl, blobUrl, durationSec },
                },
            ]);
            this.shouldScroll.set(true);
        } catch (e) {
            console.error('Voice message failed', e);
            this.voiceError.set('Could not send voice message. Please try again.');
        } finally {
            this.voiceProcessing.set(false);
            this.mediaRecorder = null;
        }
    }

    formatRecordingTime(sec: number): string {
        const m = Math.floor(sec / 60).toString().padStart(2, '0');
        const s = (sec % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    }

    private isImageMimeOrFileName(mime: string, fileName: string): boolean {
        const m = mime.toLowerCase();
        if (m.startsWith('image/')) return true;
        return /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i.test(fileName);
    }

    /** Returns true when the URL points to an audio file (GCS voice recording). */
    isAudioAttachment(url: string): boolean {
        return isBrowserPreviewableAudioAttachment(url);
    }

    /** Returns true when the URL points to a browser-previewable video file. */
    isVideoAttachment(url: string): boolean {
        return isBrowserPreviewableVideoAttachment(url);
    }

    /** Any https image URL in agent/visitor message text (KB links, GCS uploads, etc.). */
    private static readonly HTTPS_IMAGE_URL =
        /https:\/\/[^\s<>'"`\)]+\.(png|jpe?g|gif|webp|svg|bmp|ico)(\?[^\s<>'"`\)]*)?/i;

    /** GCS uploads + KB image replies render as image preview. */
    messageAttachmentForDisplay(m: Message): Message['attachment'] | undefined {
        return m.attachment;
    }

    /** Caption = message text without the image URL. */
    messageAttachmentCaption(m: Message): string | undefined {
        const att = this.messageAttachmentForDisplay(m);
        if (!att) return undefined;
        const caption = (m.text ?? '').replace(att.url, '').replace(/[`'"]/g, '').trim();
        return caption || undefined;
    }

    /** Split stored E.164-ish number into country + local digits for the phone row */
    private parseStoredPhoneIntoDialAndLocal(full: string): void {
        if (!full) {
            this.preChatPhoneLocal.set('');
            return;
        }
        const t = full.trim();
        const parsed = parsePhoneNumberFromString(t);
        if (parsed?.country) {
            this.preChatCountryIso.set(parsed.country);
            this.preChatPhoneLocal.set(parsed.formatNational());
            return;
        }
        const sorted = [...this.phoneCountries].sort((a, b) => b.dial.length - a.dial.length);
        for (const o of sorted) {
            if (t.startsWith(o.dial)) {
                this.preChatCountryIso.set(o.iso2);
                const rest = t.slice(o.dial.length).replace(/\D/g, '');
                this.preChatPhoneLocal.set(this.formatNationalWithAsYouType(rest, o.iso2));
                return;
            }
        }
        this.preChatCountryIso.set('US');
        this.preChatPhoneLocal.set(this.formatNationalWithAsYouType(t.replace(/\D/g, ''), 'US'));
    }

    /** Full phone for API / broker (E.164-style) */
    private buildPhoneForApi(): string {
        const country = this.preChatCountryIso();
        const text = this.preChatPhoneLocal().trim();
        const parsed = parsePhoneNumberFromString(text, country);
        if (parsed?.isValid()) {
            return parsed.number;
        }
        const local = text.replace(/\D/g, '');
        if (!local) return '';
        return `+${getCountryCallingCode(country)}${local}`;
    }

    /**
     * Country button label: dial code only (ISO is easy to mistake for the flag’s regional letters on Windows).
     */
    countryTriggerLabel(): string {
        return `+${getCountryCallingCode(this.preChatCountryIso())}`;
    }

    /** National-format display string for the selected country (spaces per libphonenumber). */
    private formatNationalWithAsYouType(digits: string, country: CountryCode): string {
        if (!digits) return '';
        const ayt = new AsYouType(country);
        let out = '';
        for (const ch of digits) {
            out = ayt.input(ch);
        }
        return out;
    }

    /**
     * Keeps {@link preChatPhoneLocal} nationally formatted while typing.
     * Mirrors ngx-intl-tel-input's `enableAutoCountrySelect`: when the user types an
     * international number (leading `+`), the dialed country is detected from the prefix
     * and the flag switches automatically; the dial code is then stripped so the field
     * keeps only the national digits for the resolved country.
     */
    onPreChatPhoneInput(value: string): void {
        const trimmed = value.trim();
        // User is typing — stop geo auto-detect from changing the flag underneath them.
        this.countryAutoDetectLocked = true;

        if (trimmed.startsWith('+')) {
            const ayt = new AsYouType();
            const formatted = ayt.input(trimmed);
            const detected = ayt.getCountry();
            const callingCode = ayt.getCallingCode();

            // Exact country resolved (unique dial code, e.g. +41 → CH).
            if (detected) {
                this.preChatCountryIso.set(detected);
                this.preChatPhoneLocal.set(
                    this.formatNationalWithAsYouType(ayt.getNationalNumber(), detected),
                );
                this.validatePreChat();
                return;
            }

            // Calling code known but shared across regions (e.g. +1 → US/CA/...).
            // Still switch the flag using the preferred/main country for that code.
            if (callingCode) {
                const iso = this.resolveIsoForCallingCode(callingCode);
                if (iso) {
                    this.preChatCountryIso.set(iso);
                    this.preChatPhoneLocal.set(
                        this.formatNationalWithAsYouType(ayt.getNationalNumber(), iso),
                    );
                    this.validatePreChat();
                    return;
                }
            }

            // Dial code not resolvable yet (e.g. just "+" typed) — keep raw input visible.
            this.preChatPhoneLocal.set(formatted);
            this.validatePreChat();
            return;
        }

        const digits = trimmed.replace(/\D/g, '');
        this.preChatPhoneLocal.set(this.formatNationalWithAsYouType(digits, this.preChatCountryIso()));
        this.validatePreChat();
    }

    /**
     * Picks an ISO country for a (possibly shared) calling code: keeps the current
     * selection if it already uses that code (preserves a deliberate US-vs-CA choice),
     * otherwise defers to libphonenumber's main country for the code.
     */
    private resolveIsoForCallingCode(callingCode: string): CountryCode | null {
        const current = this.preChatCountryIso();
        try {
            if (getCountryCallingCode(current) === callingCode) {
                return current;
            }
        } catch {
            // Unknown current country — fall through to the metadata lookup below.
        }
        return this.phoneMetadata.getCountryCodeForCallingCode(callingCode) ?? null;
    }

    /**
     * Best-effort default country for the phone field based on the visitor's location.
     * Tries IP geolocation first (reflects where the visitor actually is), then falls
     * back to the browser locale's region. Never overrides a stored number, a manual
     * pick, or a number the visitor has already started typing.
     */
    private async detectAndApplyGeoCountry(): Promise<void> {
        const iso = (await this.fetchCountryFromIp()) ?? this.guessCountryFromLocale();
        if (!iso) {
            return;
        }
        // Re-check the guard after the await: the visitor may have acted meanwhile.
        if (this.countryAutoDetectLocked || this.preChatPhoneLocal().trim()) {
            return;
        }
        this.preChatCountryIso.set(iso);
    }

    /**
     * Resolve the visitor's country via free, key-less, CORS-enabled IP geolocation.
     * Tries multiple providers in order so one being down/blocked doesn't break detection.
     */
    private async fetchCountryFromIp(): Promise<CountryCode | null> {
        const sources: ReadonlyArray<{
            url: string;
            pick: (data: Record<string, unknown>) => unknown;
        }> = [
            { url: 'https://api.country.is/', pick: (d) => d['country'] },
            { url: 'https://get.geojs.io/v1/ip/country.json', pick: (d) => d['country'] },
        ];

        for (const src of sources) {
            try {
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 2500);
                const res = await fetch(src.url, { signal: controller.signal });
                clearTimeout(timeout);
                if (!res.ok) {
                    continue;
                }
                const data = (await res.json()) as Record<string, unknown>;
                const code = String(src.pick(data) ?? '').toUpperCase();
                if (isSupportedCountry(code)) {
                    return code;
                }
            } catch {
                // Offline, blocked by CSP/adblock, or timed out — try the next provider.
            }
        }
        return null;
    }

    /** Zero-network fallback: derive a region from the browser locale (e.g. en-PK → PK). */
    private guessCountryFromLocale(): CountryCode | null {
        try {
            const lang = navigator.language || navigator.languages?.[0] || '';
            if (!lang) {
                return null;
            }
            const locale = new Intl.Locale(lang);
            const region = (locale.maximize?.().region ?? locale.region ?? '').toUpperCase();
            return isSupportedCountry(region) ? region : null;
        } catch {
            return null;
        }
    }

    /**
     * PNG flag URL (ISO 3166-1 alpha-2). Emoji flags often fail on Windows / embedded iframes;
     * flagcdn images work wherever img-src allows https://flagcdn.com.
     */
    phoneCountryFlagUrl(iso: string): string | null {
        const u = (iso || '').toLowerCase();
        if (u.length !== 2 || !/^[a-z]{2}$/.test(u)) {
            return null;
        }
        return `https://flagcdn.com/w20/${u}.png`;
    }

    /** Primary CTA label color: dark text on light brand fills, light text on dark fills. */
    preChatContinueTextColor(): string {
        const lum = this.colorRelativeLuminance(this.metaPrimary());
        if (lum > 0.52) {
            return '#18181b';
        }
        const sec = this.metaSecondary()?.trim();
        return sec && sec !== 'transparent' ? sec : '#ffffff';
    }

    toggleCountryDropdown(ev: Event): void {
        ev.stopPropagation();
        this.countryDropdownOpen.update((v) => !v);
        if (!this.countryDropdownOpen()) {
            this.countrySearchQuery.set('');
        }
    }

    selectPhoneCountry(c: PhoneCountryOption, ev: Event): void {
        ev.stopPropagation();
        this.countryAutoDetectLocked = true;
        const digits = this.preChatPhoneLocal().replace(/\D/g, '');
        this.preChatCountryIso.set(c.iso2);
        this.preChatPhoneLocal.set(this.formatNationalWithAsYouType(digits, c.iso2));
        this.countryDropdownOpen.set(false);
        this.countrySearchQuery.set('');
        this.validatePreChat();
    }

    onCountrySearchInput(value: string): void {
        this.countrySearchQuery.set(value);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(ev: MouseEvent): void {
        if (!this.countryDropdownOpen()) return;
        const root = this.countryPickerRoot?.nativeElement;
        if (root && !root.contains(ev.target as Node)) {
            this.countryDropdownOpen.set(false);
            this.countrySearchQuery.set('');
        }
    }

    /** Split full name for server contact fields */
    private splitFullName(): { firstName: string; lastName: string } {
        const full = this.preChatFullName().trim();
        if (!full) return { firstName: '', lastName: '' };
        const parts = full.split(/\s+/);
        return {
            firstName: parts[0] ?? '',
            lastName: parts.slice(1).join(' '),
        };
    }

    /** Public API: last N widget sessions with transcript lines */
    private messagesFromRecentConversations(
        conversations: {
            callSid: string;
            callType: string;
            startedAt: string | null;
            messages: { role: string; message: string; senderName?: string; timestamp?: string }[];
        }[],
    ): Message[] {
        const ordered = [...conversations].reverse();
        const out: Message[] = [];
        let fallbackT = Date.now() - ordered.length * 120000;
        for (const conv of ordered) {
            out.push({
                kind: ChatMessageKind.HistoryDivider,
                text: this.formatHistoryDividerLabel(conv),
                fromAi: false,
                fromHuman: false,
                sentAt: fallbackT++,
            });
            let lastDay: string | null = null;
            for (const line of conv.messages) {
                const ts = line.timestamp?.trim();
                if (ts) {
                    const day = transcriptLocalDayKey(ts);
                    if (day && day !== lastDay) {
                        lastDay = day;
                        const label = formatTranscriptDateDividerLabel(ts);
                        if (label) {
                            out.push({
                                kind: ChatMessageKind.DayDivider,
                                text: label,
                                fromAi: false,
                                fromHuman: false,
                                sentAt: new Date(ts).getTime(),
                            });
                        }
                    }
                }
                const m = this.mapTranscriptLineToMessage(line, fallbackT++);
                if (m) out.push(m);
            }
        }
        return out;
    }

    private formatHistoryDividerLabel(conv: {
        callType: string;
        startedAt: string | null;
    }): string {
        const mode = conv.callType === 'ChatWidgetAudio' ? 'Voice' : 'Text';
        let datePart = 'Earlier';
        if (conv.startedAt) {
            try {
                const d = new Date(conv.startedAt);
                datePart = d.toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                });
            } catch {
                datePart = 'Earlier';
            }
        }
        return `Previous conversation · ${datePart} · ${mode}`;
    }

    private transcriptTimestampToMs(iso?: string | null, fallbackMs?: number): number {
        const raw = typeof iso === 'string' ? iso.trim() : '';
        if (raw) {
            const d = new Date(raw);
            if (!Number.isNaN(d.getTime())) return d.getTime();
        }
        return fallbackMs ?? Date.now();
    }

    private mapTranscriptLineToMessage(
        item: { role: string; message: string; senderName?: string; timestamp?: string },
        fallbackSentAt: number,
    ): Message | null {
        const role = (item.role || '').toLowerCase();
        // Tool/system rows are internal transcript detail — never shown to the visitor on the widget.
        if (role === ConversationTranscriptRole.Tool) return null;
        const text = item.message?.trim();
        if (!text) return null;
        const sentAt = this.transcriptTimestampToMs(item.timestamp, fallbackSentAt);

        const isAiOrHuman = role === ConversationTranscriptRole.Assistant || role === ConversationTranscriptRole.Human;
        const media = this.extractMediaFromMessage(text);
        
        // Do not extract image attachments for AI/Assistant or Human/Staff messages so that they render inline in the correct sequence.
        const finalMedia = (isAiOrHuman && media?.type === 'image') ? null : media;

        const displayText = finalMedia
            ? text.replace(finalMedia.url, '').trim() || finalMedia.url
            : text;

        const base: Message =
            role === ConversationTranscriptRole.Assistant
                ? { text: displayText, fromAi: true, fromHuman: false, sentAt }
                : role === ConversationTranscriptRole.Human
                    ? {
                          text: displayText,
                          fromAi: true,
                          fromHuman: true,
                          senderName: item.senderName?.trim() || 'Team',
                          sentAt,
                      }
                    : { text: displayText, fromAi: false, sentAt };

        if (finalMedia?.type === 'audio') {
            base.voiceAttachment = { url: finalMedia.url };
        } else if (finalMedia?.type === 'image') {
            const seg = new URL(finalMedia.url).pathname.split('/').pop() || 'image';
            base.attachment = { url: finalMedia.url, fileName: decodeURIComponent(seg), isImage: true };
        }
        return base;
    }

    private extractMediaFromMessage(text: string): { url: string; type: 'audio' | 'image' | 'file' } | null {
        const kbImage = text.match(WidgetComponent.HTTPS_IMAGE_URL)?.[0];
        if (kbImage) return { url: kbImage, type: 'image' };
        return this.extractGcsMediaUrl(text);
    }

    private extractGcsMediaUrl(text: string): { url: string; type: 'audio' | 'image' | 'file' } | null {
        const match = text.match(
            /https:\/\/storage\.googleapis\.com\/[^\s]+\.(webm|ogg|mp3|mp4|m4a|wav|flac|opus|png|jpe?g|gif|webp|svg|bmp|ico|pdf|doc|docx|xls|xlsx|csv|zip|txt)(\?[^\s]*)?/i,
        );
        if (!match) return null;
        const url = match[0];
        const ext = match[1].toLowerCase();
        const AUDIO_EXTS = ['webm', 'ogg', 'mp3', 'm4a', 'wav', 'flac', 'opus'];
        const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'];
        if (AUDIO_EXTS.includes(ext)) return { url, type: 'audio' };
        if (IMAGE_EXTS.includes(ext)) return { url, type: 'image' };
        return { url, type: 'file' };
    }

    validatePreChat(): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const email = this.preChatEmail().trim();
        const nameOk = this.preChatFullName().trim().length > 0;
        const emailOk = emailRegex.test(email);
        const localDigits = this.preChatPhoneLocal().replace(/\D/g, '');
        const phoneOk = localDigits.length >= 7;
        const ok = nameOk && emailOk && phoneOk;
        this.isPreChatValid.set(ok);
        if (!ok && (email.length > 0 || this.preChatFullName().length > 0 || localDigits.length > 0)) {
            if (!nameOk) {
                this.preChatError.set('Please enter your full name');
            } else if (!emailOk) {
                this.preChatError.set('Please enter a valid email address');
            } else if (!phoneOk) {
                this.preChatError.set('Please enter a valid phone number (at least 7 digits)');
            } else {
                this.preChatError.set('');
            }
        } else {
            this.preChatError.set('');
        }
        return ok;
    }

    submitPreChat(): void {
        if (this.validatePreChat()) {
            const { firstName, lastName } = this.splitFullName();
            if (!this.dashboardPreviewMode()) {
                window.parent.postMessage(
                    {
                        type: 'widget:preChatContactSaved',
                        data: {
                            email: this.preChatEmail().trim(),
                            firstName,
                            lastName,
                            phoneNumber: this.buildPhoneForApi(),
                        },
                    },
                    '*',
                );
            }
            this.showPreChatForm.set(false);
            this.preChatError.set('');
        }
    }

    /** Validates + saves the pre-chat contact, then immediately starts the session.
     *  This collapses the form → welcome-screen → start flow into a single action. */
    submitPreChatAndStart(chatWidgetTypeValue: string): void {
        if (!this.validatePreChat()) return;
        this.preChatError.set('');
        this.showPreChatForm.set(false);
        void this.startSession(chatWidgetTypeValue);
    }

    async startSession(chatWidgetTypeValue: string): Promise<void> {
        if (this.isStyleOnlyDashboardPreview()) {
            this.chatMode.set(chatWidgetTypeValue);
            this.applyStyleOnlyPreviewState();
            return;
        }
        const anon = this.isAnonymousChatEnabled();
        if (!anon && !this.isPreChatValid()) {
            this.preChatError.set('Please complete name, email, and phone before starting');
            return;
        }

        this.preChatError.set('');
        this.startingChatMode.set(chatWidgetTypeValue);
        this.chatMode.set(chatWidgetTypeValue);
        const { firstName, lastName } = this.splitFullName();
        const email = this.preChatEmail().trim();
        this.sessionVisitorEmailForHistory = email || null;

        if (this.dashboardPreviewMode()) {
            await this.initiateDashboardPreviewConnection(chatWidgetTypeValue, {
                email,
                firstName,
                lastName,
                phoneNumber: this.buildPhoneForApi(),
            });
            return;
        }

        window.parent.postMessage({
            type: 'widget:startChatSession',
            data: {
                type: chatWidgetTypeValue,
                email: anon && !email ? '' : email,
                firstName: anon && !this.preChatFullName().trim() ? '' : firstName,
                lastName: anon && !this.preChatFullName().trim() ? '' : lastName,
                phoneNumber: anon && !this.buildPhoneForApi().trim() ? '' : this.buildPhoneForApi(),
                ...(anon ? { anonymousChat: true as const } : {}),
            },
        }, '*');
    }

    /**
     * Same HTTP contract as talkgeniejs {@code initiateWidgetConnection} — starts a real LiveKit session
     * when the widget is hosted inside the dashboard (no embed parent to bridge postMessage).
     */
    private async initiateDashboardPreviewConnection(
        chatWidgetTypeValue: string,
        contact: { email: string; firstName: string; lastName: string; phoneNumber: string },
    ): Promise<void> {
        const agentUuid = this.resolveAgentUuidForPublicApi()?.trim();
        if (!agentUuid) {
            this.preChatError.set(
                'Missing agent id. Ensure the agent is saved and try again.',
            );
            this.startingChatMode.set(null);
            this.showPreChatForm.set(true);
            return;
        }

        let timezone = 'UTC';
        try {
            timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        } catch {
            /* ignore */
        }
        const currentTime = new Date().toISOString();
        const widgetDeviceId = this.getOrCreateDashboardPreviewWidgetDeviceId(agentUuid);

        const body: Record<string, string> = {
            widgetCallType: chatWidgetTypeValue,
            timezone,
            currentTime,
            widgetDeviceId,
            email: contact.email,
            firstName: contact.firstName,
            lastName: contact.lastName,
            phoneNumber: contact.phoneNumber,
        };

        try {
            const res = await fetch(
                `${environment.apiUrl}/public/initiate-chatwidget-connection/${encodeURIComponent(agentUuid)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                },
            );

            const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;

            if (!res.ok) {
                const msg =
                    typeof json['message'] === 'string' && json['message'].trim()
                        ? json['message'].trim()
                        : `Could not start chat (${res.status}). Check wallet balance and API keys.`;
                this.preChatError.set(msg);
                this.startingChatMode.set(null);
                this.showPreChatForm.set(true);
                return;
            }

            const raw =
                json['data'] && typeof json['data'] === 'object'
                    ? (json['data'] as Record<string, unknown>)
                    : json;

            const wsUrl = String(raw['wsUrl'] ?? '');
            const token = String(raw['token'] ?? '');
            if (!wsUrl || !token) {
                this.preChatError.set('Invalid response from server when starting chat.');
                this.startingChatMode.set(null);
                this.showPreChatForm.set(true);
                return;
            }

            this.isHttpRequestInProcess.set(false);
            await this.onSessionStarted({
                wsUrl,
                token,
                chatMode: chatWidgetTypeValue,
                attachmentUploadToken:
                    typeof raw['attachmentUploadToken'] === 'string'
                        ? raw['attachmentUploadToken']
                        : undefined,
                roomName:
                    typeof raw['roomName'] === 'string' ? raw['roomName'] : undefined,
                liveKitRoomSids:
                    typeof raw['liveKitRoomSids'] === 'string'
                        ? raw['liveKitRoomSids']
                        : undefined,
            });
        } catch (e) {
            console.error('Dashboard preview session start failed', e);
            this.preChatError.set('Network error while starting chat. Try again.');
            this.startingChatMode.set(null);
            this.showPreChatForm.set(true);
        }
    }

    /** Same storage key pattern as talkgeniejs embed for consistent device scoping. */
    private getOrCreateDashboardPreviewWidgetDeviceId(agentUuid: string): string {
        const key = `talkgenie_widget_device_${agentUuid}`;
        try {
            const existing = localStorage.getItem(key);
            if (existing?.trim()) {
                return existing.trim();
            }
            const id = crypto.randomUUID();
            localStorage.setItem(key, id);
            return id;
        } catch {
            return crypto.randomUUID();
        }
    }

    async onSessionStarted(data: {
        wsUrl: string;
        token: string;
        chatMode?: string;
        attachmentUploadToken?: string;
        roomName?: string;
        liveKitRoomSids?: string;
    }): Promise<void> {
        this.socketConnectedSub?.unsubscribe();

        this.isSocketRequestInProcess.set(true);
        this.widgetAttachmentUploadToken = data.attachmentUploadToken?.trim() || null;
        // Prefer liveKitRoomSids (RM_xxx — matches call_logs.callSid set by broker)
        // fall back to roomName for backward compatibility
        this.sessionRoomName = data.liveKitRoomSids?.trim() || data.roomName?.trim() || null;
        const modeFromServer = data.chatMode?.trim();
        if (modeFromServer) {
            this.chatMode.set(modeFromServer);
        }
        const resolvedMode = this.chatMode();
        if (
            resolvedMode !== chatWidgetType.ChatWidgetText &&
            resolvedMode !== chatWidgetType.ChatWidgetAudio
        ) {
            this.chatMode.set(chatWidgetType.ChatWidgetText);
        }

        const email = this.sessionVisitorEmailForHistory?.trim();
        const agentUuid = this.resolveAgentUuidForPublicApi();
        const uploadAuth = this.widgetAttachmentUploadToken;
        const history =
            agentUuid && email && uploadAuth
                ? await this.loadRecentConversationHistory(agentUuid, email, uploadAuth)
                : [];
        this.sessionStarted.set(true);
        this.responses.set(history);
        this.startingChatMode.set(null);
        const visitorName = this.preChatFullName().trim();
        this.callSvc.startWebCall({
            ...data,
            chatMode: data.chatMode ?? this.chatMode(),
            ...(visitorName ? { participantDisplayName: visitorName } : {}),
        });

        this.socketConnectedSub = this.callSvc.onSocketConnected.subscribe(() => {
            this.isSocketRequestInProcess.set(false);
            this.scheduleScrollChatToBottom();
        });
    }

    endSession(): void {
        if (this.isStyleOnlyDashboardPreview()) {
            this.applyStyleOnlyPreviewState();
            return;
        }
        // Guard against re-entrancy: when feedback form is already showing (the user clicked end
        // and the LiveKit RoomEvent.Disconnected cascade re-triggers this via onEndCall), do nothing.
        if (this.showFeedbackForm()) return;

        this.clearVisitorTypingOutbound();
        this.humanAgentsTypingByIdentity.set({});
        this.socketConnectedSub?.unsubscribe();
        this.socketConnectedSub = undefined;

        // Stop the LiveKit session immediately
        this.callSvc.endSession(false);

        // Show feedback form if session has a valid room name and was truly started
        const canShowFeedback = !!this.sessionRoomName && this.sessionStarted();
        if (canShowFeedback) {
            // Minimal teardown — keep enough state to identify the session for feedback
            this.endCall.set(true);
            this.sessionStarted.set(false);
            this.responses.set([]);
            this.visitorHumanActive = false;
            this.activeStaffByIdentity.set({});
            this.nextAnonStaffKey = 0;
            this.isHumanAgentPresent.set(false);
            this.isSocketRequestInProcess.set(false);
            this.startingChatMode.set(null);
            this.unreadWhileMinimized.set(0);
            this.postUnreadCountToParent(0);
            // Reset feedback form state and show it
            this.feedbackRating.set(0);
            this.feedbackIsResolved.set(null);
            this.feedbackComment.set('');
            this.feedbackSubmitting.set(false);
            this.feedbackSubmitted.set(false);
            this.showFeedbackForm.set(true);
        } else {
            this.finalizeEndSession();
        }
    }

    /** Full cleanup after feedback is submitted or skipped. */
    finalizeEndSession(): void {
        this.showFeedbackForm.set(false);
        this.feedbackRating.set(0);
        this.feedbackIsResolved.set(null);
        this.feedbackComment.set('');
        this.feedbackSubmitting.set(false);
        this.feedbackSubmitted.set(false);
        this.endCall.set(true);
        this.sessionVisitorEmailForHistory = null;
        this.responses.set([]);
        this.visitorHumanActive = false;
        this.activeStaffByIdentity.set({});
        this.nextAnonStaffKey = 0;
        this.isHumanAgentPresent.set(false);
        this.sessionStarted.set(false);
        this.widgetAttachmentUploadToken = null;
        this.sessionRoomName = null;
        this.isSocketRequestInProcess.set(false);
        this.startingChatMode.set(null);
        this.messageSoundGesturePrimed = false;
        this.unreadWhileMinimized.set(0);
        this.postUnreadCountToParent(0);
        if (this.messageSoundPreviewTimer != null) {
            clearTimeout(this.messageSoundPreviewTimer);
            this.messageSoundPreviewTimer = null;
        }
    }

    selectFeedbackRating(rating: number): void {
        this.feedbackRating.set(rating);
    }

    selectFeedbackResolved(resolved: boolean): void {
        this.feedbackIsResolved.set(resolved);
    }

    async submitFeedback(): Promise<void> {
        const rating = this.feedbackRating();
        const isResolved = this.feedbackIsResolved();
        const agentUuid = this.resolveAgentUuidForPublicApi();
        const callSid = this.sessionRoomName;
        const token = this.widgetAttachmentUploadToken;

        if (!rating || isResolved === null || !agentUuid || !callSid || !token) {
            return;
        }

        this.feedbackSubmitting.set(true);
        try {
            const comment = this.feedbackComment().trim() || undefined;
            if (this.useWidgetParentApiProxy()) {
                await this.submitFeedbackViaParent(
                    agentUuid,
                    token,
                    callSid,
                    rating,
                    isResolved,
                    comment,
                );
            } else {
                await this.submitFeedbackDirect(
                    agentUuid,
                    token,
                    callSid,
                    rating,
                    isResolved,
                    comment,
                );
            }
            this.feedbackSubmitted.set(true);
            setTimeout(() => this.finalizeEndSession(), 2000);
        } catch {
            // Silently skip on error — don't block the user
            this.finalizeEndSession();
        } finally {
            this.feedbackSubmitting.set(false);
        }
    }

    skipFeedback(): void {
        this.finalizeEndSession();
    }

    openLightbox(url: string): void { this.lightboxImageUrl.set(url); }
    closeLightbox(): void { this.lightboxImageUrl.set(null); }

    @HostListener('click', ['$event'])
    onHostClick(ev: MouseEvent): void {
        const target = ev.target as HTMLElement;
        if (target && target.classList.contains('tg-chat-inline-img')) {
            const src = target.getAttribute('src');
            if (src) {
                this.openLightbox(src);
                ev.stopPropagation();
            }
        }
    }

    toggleMute(): void {
        this.isMuted.update(value => {
            const newValue = !value;
            this.callSvc.setMuted(newValue);
            return newValue;
        });
    }

    private addStaffToActiveMap(agentIdentity?: string, agentName?: string): void {
        const id = agentIdentity?.trim() || `staff-${++this.nextAnonStaffKey}`;
        const name = (agentName?.trim() || 'Team member').trim();
        this.activeStaffByIdentity.update((m) => ({ ...m, [id]: name }));
    }

    private removeStaffFromActiveMap(leftIdentity?: string, agentName?: string): void {
        this.activeStaffByIdentity.update((m) => {
            const next = { ...m };
            const lid = leftIdentity?.trim();
            if (lid && lid in next) {
                delete next[lid];
                return next;
            }
            const target = agentName?.trim();
            if (target) {
                for (const [k, v] of Object.entries(next)) {
                    if (v === target) {
                        delete next[k];
                        break;
                    }
                }
            }
            return next;
        });
    }

    private applyStyleOnlyPreviewState(): void {
        const cfg = this.widgetConfig();
        if (!cfg) {
            return;
        }
        const previewScreen = this.dashboardPreviewScreen();
        const showConversation = previewScreen === 'conversation';
        const showBubble = previewScreen === 'bubble';
        const anonymousChat = this.isAnonymousChatEnabled();
        const isVoice = !!cfg.voiceEnabled && !cfg.messageEnabled;
        // In style preview, the contact form should be optional based on enableAnonymousChat.
        // - welcome + anonymous=false => show contact form
        // - welcome + anonymous=true => skip contact form
        // - conversation => no contact form (conversation skin preview)
        // - bubble => show only the launcher (closed state)
        const showContactForm = !showConversation && !anonymousChat && !showBubble;
        this.showPreChatForm.set(showContactForm);
        this.showFeedbackForm.set(false);
        this.feedbackSubmitted.set(false);
        this.feedbackSubmitting.set(false);
        this.showHeaderMenu.set(false);
        this.isSocketRequestInProcess.set(false);
        this.sessionStarted.set(showConversation);
        this.isHumanAgentPresent.set(false);
        this.chatMode.set(isVoice ? chatWidgetType.ChatWidgetAudio : chatWidgetType.ChatWidgetText);
        this.startingChatMode.set(null);
        this.unreadWhileMinimized.set(0);
        this.postUnreadCountToParent(0);
        this.isPreChatValid.set(anonymousChat);
        if (showConversation) {
            const t = Date.now();
            const sample: Message[] = [
                {
                    text: 'Hello! I can help with sales, support, and booking questions.',
                    fromAi: true,
                    fromHuman: false,
                    sentAt: t - 2,
                },
                {
                    text: 'Great, can I customize this widget without starting a real call?',
                    fromAi: false,
                    fromHuman: false,
                    sentAt: t - 1,
                },
            ];
            this.responses.set(sample);
        } else {
            this.responses.set([]);
        }
        if (showBubble) {
            this.isOpen.set(false);
        } else {
            this.isOpen.set(true);
        }
        this.shouldScroll.set(true);
    }

    /**
     * When switching from style-only -> interactive-live, clear forced style-preview state
     * so live mode starts from its own welcome/pre-chat flow.
     */
    private resetInteractivePreviewState(): void {
        this.sessionStarted.set(false);
        this.responses.set([]);
        this.showFeedbackForm.set(false);
        this.feedbackSubmitted.set(false);
        this.feedbackSubmitting.set(false);
        this.showHeaderMenu.set(false);
        this.startingChatMode.set(null);
        this.isSocketRequestInProcess.set(false);
        this.unreadWhileMinimized.set(0);
        this.postUnreadCountToParent(0);

        if (this.isAnonymousChatEnabled()) {
            this.showPreChatForm.set(false);
            this.isPreChatValid.set(true);
            this.preChatError.set('');
        } else {
            this.showPreChatForm.set(true);
            this.isPreChatValid.set(false);
        }
    }
}